/**
 * BillingFinancial/index.tsx
 * Internal financial dashboard — 5 tabs: Deferred Revenue, MRR/ARR,
 * Collections, QuickBooks, StaxBill Import.
 * Calls velaro-messaging superadmin/billing/* endpoints via api.messaging.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

// ── Types ──────────────────────────────────────────────────────────────────────

type DashTab = "upcoming" | "collections" | "deferred" | "mrr" | "revenue" | "quickbooks" | "health" | "staxbill" | "packages" | "tax";

interface FinancialSummary {
  deferred: { balanceCents: number; recognizedThisMonthCents: number };
  mrr: { mrrCents: number; arrCents: number };
  collections: { openCents: number; overdueCount: number; paidThisMonthCents: number };
  quickBooks: { pendingSyncCount: number; failedSyncCount: number };
}

interface DeferredScheduleRow {
  year: number;
  month: number;
  scheduledCents: number;
  count: number;
}

interface AgingBucket {
  bucket: string;
  label: string;
  count: number;
  amountCents: number;
}

interface DunningPipelineRow {
  state: string;
  count: number;
}

interface TierRow {
  packageId: number;
  count: number;
  mrrCents: number;
}

interface UpcomingRenewal {
  id: number;
  siteId: number;
  company: string | null;
  email: string | null;
  billingInterval: string;
  billedAmount: number;
  paymentMethod: string;
  nextRenewalDate: string;
  daysUntilRenewal: number;
  parallelBillingMode: boolean;
  externalBillingSystem: string | null;
}

interface OpenInvoice {
  id: number;
  siteId: number;
  company: string;
  email: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  amountDue: number;
  totalAmount: number;
  status: string;
  daysOverdue: number;
  dunningState: string;
  agingBucket: string;
}

interface TierRow {
  packageId: number;
  count: number;
  mrrCents: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function monthName(m: number): string {
  return new Date(2000, m - 1, 1).toLocaleString("en-US", { month: "short" });
}

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => i + 1);
const YEAR_OPTS = [2024, 2025, 2026, 2027];

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color = "slate",
}: {
  label: string;
  value: string;
  sub?: string;
  color?: "slate" | "emerald" | "orange" | "blue";
}) {
  const textColors = {
    slate: "text-slate-800",
    emerald: "text-emerald-600",
    orange: "text-orange-600",
    blue: "text-blue-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${textColors[color]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Deferred Revenue Tab ───────────────────────────────────────────────────────

function DeferredTab({
  summary,
  api,
  toastAlert,
}: {
  summary: FinancialSummary | null;
  api: ReturnType<typeof useApi>;
  toastAlert: ReturnType<typeof useToastAlert>;
}) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [schedule, setSchedule] = useState<DeferredScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [recognizing, setRecognizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(
        `superadmin/billing/deferred-revenue?month=${month}&year=${year}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setSchedule(Array.isArray(data.schedule) ? data.schedule : []);
      }
    } catch {
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, month, year]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRecognize() {
    if (
      !confirm(
        `Mark all unrecognized deferred revenue entries for ${monthName(month)} ${year} as recognized? This cannot be undone.`
      )
    )
      return;
    setRecognizing(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/recognize-revenue", {
        month,
        year,
      });
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(
          Severity.Success,
          `Recognized ${data.entriesRecognized} entries (${fmt(data.totalCents ?? 0)})`
        );
        load();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to run revenue recognition.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setRecognizing(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Deferred Revenue Balance"
          value={fmt(summary?.deferred.balanceCents ?? 0)}
          sub="Total unrecognized"
          color="blue"
        />
        <StatCard
          label="Recognized This Month"
          value={fmt(summary?.deferred.recognizedThisMonthCents ?? 0)}
          sub="Posted to income"
          color="emerald"
        />
      </div>

      {/* Schedule */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">Upcoming Recognition Schedule</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : schedule.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No scheduled entries.</p>
        ) : (
          <>
            <div className="grid grid-cols-[80px_80px_1fr_80px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              <span>Year</span>
              <span>Month</span>
              <span className="text-right">Scheduled</span>
              <span className="text-right">Entries</span>
            </div>
            <div className="divide-y divide-slate-100">
              {schedule.map((row) => (
                <div
                  key={`${row.year}-${row.month}`}
                  className="grid grid-cols-[80px_80px_1fr_80px] gap-3 px-4 py-2 items-center"
                >
                  <span className="text-sm text-slate-700">{row.year}</span>
                  <span className="text-sm text-slate-700">{monthName(row.month)}</span>
                  <span className="text-sm font-medium text-slate-800 text-right tabular-nums">
                    {fmt(row.scheduledCents)}
                  </span>
                  <span className="text-sm text-slate-500 text-right">{row.count}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Month-end recognition */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-sm font-medium text-slate-700 mb-3">Run Month-End Recognition</p>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_OPTS.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {YEAR_OPTS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            onClick={handleRecognize}
            disabled={recognizing}
            className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
          >
            {recognizing && <i className="fa-solid fa-spinner fa-spin" />}
            Post Recognition
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Marks all unrecognized entries for the chosen month/year as recognized. Idempotent.
        </p>
      </div>
    </div>
  );
}

// ── MRR/ARR Tab ───────────────────────────────────────────────────────────────

// ── Revenue Dashboard Tab ─────────────────────────────────────────────────────

interface RevenueDashboard {
  asOf: string;
  mrr: { cents: number; dollars: number };
  arr: { cents: number; dollars: number };
  activeSubscriptions: number;
  nonRenewing: {
    count: number;
    arrLoss: { cents: number; dollars: number };
    adjustedArr: { cents: number; dollars: number };
  };
  ytd: {
    bookings: { cents: number; dollars: number };
    revenueBilled: { cents: number; dollars: number };
    revenuePaid: { cents: number; dollars: number };
    writtenOff: { cents: number; dollars: number };
    cancellations: { type: string; count: number }[];
    churnRatePercent: number;
  };
  forecast: {
    thisWeek: { cents: number; dollars: number };
    thisMonth: { cents: number; dollars: number };
    nextMonth: { cents: number; dollars: number };
    toYearEnd: { cents: number; dollars: number };
    next12Months: { cents: number; dollars: number };
  };
}

function RevenueDashboardTab() {
  const api = useApi();
  const [data, setData] = useState<RevenueDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/revenue-dashboard");
      if (resp.ok) setData(await resp.json());
      else setData(null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-slate-400 text-center py-10">Unable to load revenue data.</p>;
  }

  const attritionTotal = data.ytd.cancellations.reduce((s, c) => s + c.count, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">As of {new Date(data.asOf).toLocaleString()}</p>
        <button onClick={load} className="text-xs text-primary hover:underline flex items-center gap-1">
          <i className="fa-solid fa-arrows-rotate text-xs" /> Refresh
        </button>
      </div>

      {/* Core metrics */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Core Metrics</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="MRR" value={fmt(data.mrr.cents)} sub="Monthly recurring revenue" color="blue" />
          <StatCard label="ARR" value={fmt(data.arr.cents)} sub="Annualized run rate" color="blue" />
          <StatCard
            label="Adjusted ARR"
            value={fmt(data.nonRenewing.adjustedArr.cents)}
            sub={data.nonRenewing.count > 0 ? `After ${data.nonRenewing.count} non-renewing` : "No non-renewing"}
            color={data.nonRenewing.count > 0 ? "orange" : "emerald"}
          />
          <StatCard label="Active Subscriptions" value={String(data.activeSubscriptions)} sub="Active + trialing" color="slate" />
        </div>
      </div>

      {/* Non-renewing impact */}
      {data.nonRenewing.count > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-orange-800 mb-1">
            <i className="fa-solid fa-triangle-exclamation mr-2" />
            Non-Renewing ARR at Risk
          </p>
          <p className="text-sm text-orange-700">
            {data.nonRenewing.count} subscription{data.nonRenewing.count !== 1 ? "s" : ""} set to cancel —{" "}
            <strong>{fmt(data.nonRenewing.arrLoss.cents)}</strong> ARR will be lost when they lapse.
            Adjusted ARR (if all lapsed today) = <strong>{fmt(data.nonRenewing.adjustedArr.cents)}</strong>.
          </p>
        </div>
      )}

      {/* Revenue forecast */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Revenue Forecast</p>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-right px-4 py-2">Expected Revenue</th>
                <th className="text-left px-4 py-2 text-slate-400">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[
                { label: "This Week", val: data.forecast.thisWeek.cents, note: "Renewals due this week" },
                { label: "This Month", val: data.forecast.thisMonth.cents, note: "Renewals due this month" },
                { label: "Next Month", val: data.forecast.nextMonth.cents, note: "" },
                { label: `To Year End (${new Date().getFullYear()})`, val: data.forecast.toYearEnd.cents, note: "Remaining this year" },
                { label: "Next 12 Months", val: data.forecast.next12Months.cents, note: "Rolling 12-month revenue" },
              ].map(({ label, val, note }) => (
                <tr key={label}>
                  <td className="px-4 py-3 font-medium text-slate-700">{label}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-800">{fmt(val)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Forecast based on current BilledAmount and NextRenewalDate. Non-renewing subscriptions excluded after their cancellation date.
        </p>
      </div>

      {/* YTD performance */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Year-to-Date ({new Date().getFullYear()})</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Bookings" value={fmt(data.ytd.bookings.cents)} sub="Invoices issued YTD" color="blue" />
          <StatCard label="Revenue Billed" value={fmt(data.ytd.revenueBilled.cents)} sub="Paid + open invoices" color="blue" />
          <StatCard label="Revenue Paid (Cash)" value={fmt(data.ytd.revenuePaid.cents)} sub="Collected YTD" color="emerald" />
          <StatCard label="Written Off" value={fmt(data.ytd.writtenOff.cents)} sub="Bad debt + cancellations" color="orange" />
        </div>
      </div>

      {/* Attrition */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3">Attrition YTD</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            label="Churn Rate YTD"
            value={`${data.ytd.churnRatePercent}%`}
            sub="Cancelled / active at start of year"
            color={data.ytd.churnRatePercent > 5 ? "orange" : "emerald"}
          />
          <StatCard label="Total Cancellations" value={String(attritionTotal)} sub="All attrition types" color="slate" />
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">By Type</p>
            {data.ytd.cancellations.length === 0 ? (
              <p className="text-sm text-slate-400">No cancellations YTD</p>
            ) : (
              <div className="space-y-1">
                {data.ytd.cancellations.map((c) => (
                  <div key={c.type} className="flex justify-between text-sm">
                    <span className="text-slate-600 capitalize">{c.type.replace(/_/g, " ")}</span>
                    <span className="font-medium text-slate-800">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Definitions */}
      <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <p className="font-semibold text-slate-600 mb-2">Metric Definitions</p>
        <p><strong>MRR</strong> = Sum of all active subscription monthly equivalents (quarterly ÷3, annual ÷12, etc.)</p>
        <p><strong>ARR</strong> = MRR × 12 (annualized run rate — what today's state earns over a year)</p>
        <p><strong>Adjusted ARR</strong> = ARR minus ARR from non-renewing subscriptions (forward-looking)</p>
        <p><strong>Bookings</strong> = Total invoice face value issued YTD (regardless of payment)</p>
        <p><strong>Revenue Billed</strong> = Paid + open invoices YTD (recognized or expected to be recognized)</p>
        <p><strong>Revenue Paid</strong> = Cash actually collected YTD (paid invoices only)</p>
        <p><strong>Forecast</strong> = Scheduled renewal amounts based on current BilledAmount and NextRenewalDate</p>
      </div>
    </div>
  );
}

function MrrTab({ summary }: { summary: FinancialSummary | null }) {
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [collected, setCollected] = useState({ last: 0, this: 0 });
  const api = useApi();

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("superadmin/billing/mrr-report");
        if (resp.ok) {
          const data = await resp.json();
          setTiers(Array.isArray(data.byTier) ? data.byTier : []);
          setCollected({
            last: data.lastMonthCollectedCents ?? 0,
            this: data.thisMonthCollectedCents ?? 0,
          });
        }
      } catch {
        setTiers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [api.messaging]);

  const packageNames: Record<number, string> = { 1: "Starter", 2: "Professional", 3: "Enterprise" };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="MRR"
          value={fmt(summary?.mrr.mrrCents ?? 0)}
          sub="Monthly recurring revenue"
          color="blue"
        />
        <StatCard
          label="ARR"
          value={fmt(summary?.mrr.arrCents ?? 0)}
          sub="Annualized"
          color="blue"
        />
        <StatCard
          label="Last Month Collected"
          value={fmt(collected.last)}
          sub="Cash collected"
          color="emerald"
        />
        <StatCard
          label="This Month Collected"
          value={fmt(collected.this)}
          sub="Cash collected"
          color="emerald"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">MRR by Plan Tier</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No active subscriptions.</p>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_100px_130px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
              <span>Plan</span>
              <span className="text-right">Accounts</span>
              <span className="text-right">MRR</span>
            </div>
            <div className="divide-y divide-slate-100">
              {tiers.map((t) => (
                <div
                  key={t.packageId}
                  className="grid grid-cols-[1fr_100px_130px] gap-3 px-4 py-3 items-center"
                >
                  <span className="text-sm font-medium text-slate-700">
                    {packageNames[t.packageId] ?? `Package ${t.packageId}`}
                  </span>
                  <span className="text-sm text-slate-600 text-right">{t.count}</span>
                  <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                    {fmt(t.mrrCents)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Upcoming Renewals Tab ─────────────────────────────────────────────────────

function UpcomingTab() {
  const [renewals, setRenewals] = useState<UpcomingRenewal[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const api = useApi();

  const load = async (d: number) => {
    setLoading(true);
    try {
      const resp = await api.messaging.get(`superadmin/billing/upcoming?days=${d}`);
      if (resp.ok) {
        const data = await resp.json();
        setRenewals(Array.isArray(data.renewals) ? data.renewals : []);
      }
    } catch {
      setRenewals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days, api.messaging]);

  const totalMrr = renewals.reduce((s, r) => s + (r.billedAmount ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Show next</span>
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                days === d
                  ? "bg-primary text-white border-primary"
                  : "border-slate-300 text-slate-600 hover:border-primary-hover"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500">
          <span className="font-semibold text-slate-800">{renewals.length}</span> upcoming ·{" "}
          <span className="font-semibold text-emerald-600">${(totalMrr).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span> total
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_100px_120px_100px_80px] gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
          <span>Customer</span>
          <span>Interval</span>
          <span className="text-right">Amount</span>
          <span>Renewal Date</span>
          <span>Payment</span>
          <span className="text-center">Days</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : renewals.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No upcoming renewals in this window.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {renewals.map((r) => (
              <div key={r.id} className="grid grid-cols-[2fr_1fr_100px_120px_100px_80px] gap-3 px-4 py-3 items-center">
                <div>
                  <p className="text-sm font-medium text-slate-800 truncate">{r.company ?? `Site ${r.siteId}`}</p>
                  <p className="text-xs text-slate-400 truncate">{r.email}</p>
                  {r.parallelBillingMode && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">parallel mode</span>
                  )}
                </div>
                <span className="text-xs text-slate-600 capitalize">{r.billingInterval}</span>
                <span className="text-sm font-semibold text-slate-800 text-right tabular-nums">
                  ${(r.billedAmount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-sm text-slate-600">
                  {r.nextRenewalDate ? new Date(r.nextRenewalDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </span>
                <span className="text-xs capitalize text-slate-600">{r.paymentMethod}</span>
                <span className={`text-sm font-bold text-center ${r.daysUntilRenewal <= 3 ? "text-orange-600" : "text-slate-700"}`}>
                  {r.daysUntilRenewal}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {renewals.some(r => r.parallelBillingMode) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Accounts in <strong>parallel mode</strong> will generate invoices internally but emails are suppressed.
          Use the StaxBill Import tab to cut them over when ready.
        </div>
      )}
    </div>
  );
}

// ── Collections Tab ───────────────────────────────────────────────────────────

function CollectionsTab({ summary }: { summary: FinancialSummary | null }) {
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [dunning, setDunning] = useState<DunningPipelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucketFilter, setBucketFilter] = useState<string>("all");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const api = useApi();
  const toastAlert = useToastAlert();

  const load = async () => {
    setLoading(true);
    try {
      const [invResp, colResp] = await Promise.all([
        api.messaging.get("superadmin/billing/invoices-open"),
        api.messaging.get("superadmin/billing/collections-report"),
      ]);
      if (invResp.ok) {
        const data = await invResp.json();
        setInvoices(Array.isArray(data) ? data : []);
      }
      if (colResp.ok) {
        const data = await colResp.json();
        setDunning(Array.isArray(data.dunningPipeline) ? data.dunningPipeline : []);
      }
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [api.messaging]);

  const markPaid = async (inv: OpenInvoice) => {
    setActionLoading(inv.id);
    try {
      const resp = await api.messaging.post(`superadmin/billing/invoice/${inv.id}/pay`, {
        amountCents: inv.amountDue,
        notes: "Manual payment recorded via Financial Dashboard"
      });
      const data = await resp.json();
      if (data.success) {
        toastAlert.displayToast(Severity.Success, `Invoice ${inv.invoiceNumber} marked paid — dunning stopped.`);
        await load();
      } else {
        toastAlert.displayToast(Severity.Error, data.error ?? "Failed to mark paid");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const resend = async (inv: OpenInvoice) => {
    setActionLoading(inv.id);
    try {
      const resp = await api.messaging.post(`superadmin/billing/invoice/${inv.id}/resend`, {});
      const data = await resp.json();
      if (data.success) {
        toastAlert.displayToast(Severity.Success, `Invoice resent to ${data.sentTo}`);
      } else {
        toastAlert.displayToast(Severity.Error, data.error ?? "Failed to resend");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const BUCKETS = [
    { id: "all", label: "All" },
    { id: "current", label: "Current" },
    { id: "1-30", label: "1–30 days" },
    { id: "31-60", label: "31–60 days" },
    { id: "61-90", label: "61–90 days" },
    { id: "90+", label: "90+ days" },
  ];

  const filtered = bucketFilter === "all" ? invoices : invoices.filter(i => i.agingBucket === bucketFilter);

  const bucketColor = (bucket: string) => {
    if (bucket === "current") return "text-slate-700 bg-slate-100";
    if (bucket === "1-30") return "text-yellow-700 bg-yellow-100";
    if (bucket === "31-60") return "text-orange-700 bg-orange-100";
    return "text-red-700 bg-red-100";
  };

  const dunningBadge = (state: string) => {
    if (!state || state === "none") return null;
    const colors: Record<string, string> = {
      warn1: "bg-yellow-100 text-yellow-700",
      warn2: "bg-orange-100 text-orange-700",
      suspended: "bg-red-100 text-red-700",
    };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${colors[state] ?? "bg-slate-100 text-slate-600"}`}>
        {state}
      </span>
    );
  };

  const exportCsv = () => {
    const base = (import.meta.env.VITE_MESSAGING_API_URL as string).replace(/\/$/, "");
    const from = new Date(); from.setFullYear(from.getFullYear() - 1);
    const to = new Date();
    const params = new URLSearchParams({
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10)
    });
    window.open(`${base}/superadmin/billing/export/invoices?${params}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end mb-1">
        <button
          onClick={exportCsv}
          className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"
          title="Export last 12 months of invoices to CSV"
        >
          <i className="fa-solid fa-file-csv" />
          Export CSV
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Total Open"
          value={fmt(summary?.collections.openCents ?? 0)}
          sub="Outstanding invoices"
          color="blue"
        />
        <StatCard
          label="Overdue"
          value={String(summary?.collections.overdueCount ?? 0)}
          sub="Past due date"
          color={summary?.collections.overdueCount ? "orange" : "slate"}
        />
        <StatCard
          label="Paid This Month"
          value={fmt(summary?.collections.paidThisMonthCents ?? 0)}
          sub="Cash collected"
          color="emerald"
        />
      </div>

      {/* Dunning pipeline summary */}
      {dunning.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {dunning.map((d) => (
            <div key={d.state} className="bg-white border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 capitalize">{d.state?.replace(/_/g, " ")}</span>
              <span className="text-sm font-bold text-slate-800">{d.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bucket filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {BUCKETS.map((b) => {
          const cnt = b.id === "all" ? invoices.length : invoices.filter(i => i.agingBucket === b.id).length;
          return (
            <button
              key={b.id}
              onClick={() => setBucketFilter(b.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                bucketFilter === b.id
                  ? "bg-primary text-white border-primary"
                  : "border-slate-300 text-slate-600 hover:border-primary-hover"
              }`}
            >
              {b.label} ({cnt})
            </button>
          );
        })}
        <button
          onClick={load}
          className="ml-auto px-3 py-1 rounded-full text-xs border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          <i className="fa-solid fa-arrows-rotate mr-1" /> Refresh
        </button>
      </div>

      {/* Invoice drilldown table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_90px_90px_100px_1fr_150px] gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
          <span>Customer</span>
          <span>Invoice #</span>
          <span className="text-right">Amount</span>
          <span className="text-center">Days Over</span>
          <span>Dunning</span>
          <span>Due Date</span>
          <span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="fa-solid fa-spinner fa-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No open invoices.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((inv) => (
              <div key={inv.id} className="grid grid-cols-[2fr_1fr_90px_90px_100px_1fr_150px] gap-2 px-4 py-3 items-center">
                <div>
                  <p className="text-sm font-medium text-slate-800 truncate">{inv.company}</p>
                  <p className="text-xs text-slate-400 truncate">{inv.email}</p>
                </div>
                <span className="text-xs font-mono text-slate-600">{inv.invoiceNumber}</span>
                <span className="text-sm font-semibold text-slate-800 text-right tabular-nums">
                  {fmt(inv.amountDue)}
                </span>
                <span className={`text-sm font-bold text-center ${inv.daysOverdue > 60 ? "text-red-600" : inv.daysOverdue > 30 ? "text-orange-600" : inv.daysOverdue > 0 ? "text-yellow-600" : "text-slate-500"}`}>
                  {inv.daysOverdue > 0 ? inv.daysOverdue : "—"}
                </span>
                <span>{dunningBadge(inv.dunningState)}</span>
                <span className="text-xs text-slate-600">
                  {new Date(inv.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
                <div className="flex items-center gap-1 justify-end">
                  <button
                    onClick={() => markPaid(inv)}
                    disabled={actionLoading === inv.id}
                    title="Mark Paid — stops dunning immediately"
                    className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {actionLoading === inv.id ? <i className="fa-solid fa-spinner fa-spin" /> : "Mark Paid"}
                  </button>
                  <button
                    onClick={() => resend(inv)}
                    disabled={actionLoading === inv.id}
                    title="Resend invoice email"
                    className="px-2 py-1 text-xs rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Resend
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Mark Paid records the payment and immediately stops dunning for that account.
        Use this when a check, ACH, or wire is received outside of Stripe.
      </p>
    </div>
  );
}

// ── QuickBooks Tab ─────────────────────────────────────────────────────────────

interface QbSyncRow {
  id: number;
  siteId: number;
  invoiceNumber: string;
  invoiceDate: string;
  status: string;
  totalAmount: number;
  qbInvoiceId: string | null;
  qbSyncStatus: string | null;
  qbSyncedAt: string | null;
  qbSyncError: string | null;
}

interface QbSyncStatus {
  range: { from: string; to: string };
  summary: { synced: number; pending: number; failed: number; skipped: number; total: number };
  needsAction: QbSyncRow[];
}

function QuickBooksTab({ summary, onRefresh }: { summary: FinancialSummary | null; onRefresh: () => void }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [syncing, setSyncing] = useState(false);
  const [qbStatus, setQbStatus] = useState<QbSyncStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [resyncingId, setResyncingId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));

  async function handleSync() {
    setSyncing(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/sync-quickbooks", {});
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(
          Severity.Success,
          `Synced ${data.synced} invoice(s)${data.failed > 0 ? ` · ${data.failed} failed` : ""}`
        );
        onRefresh();
        loadQbStatus();
      } else {
        toastAlert.displayToast(Severity.Error, "QuickBooks sync failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSyncing(false);
    }
  }

  async function loadQbStatus() {
    setLoadingStatus(true);
    try {
      const resp = await api.messaging.get(
        `superadmin/billing/quickbooks-sync-status?from=${fromDate}&to=${toDate}`
      );
      if (resp.ok) setQbStatus(await resp.json());
    } catch { /* ignore */ } finally {
      setLoadingStatus(false);
    }
  }

  async function handleResync(row: QbSyncRow) {
    // If already synced, require explicit force confirmation to avoid QB duplicates
    const needsForce = !!row.qbInvoiceId && row.qbSyncStatus === "synced";
    if (needsForce && !window.confirm(
      `Invoice ${row.invoiceNumber} was already synced to QB (ID: ${row.qbInvoiceId}).\n\n` +
      `Force resync will create a DUPLICATE in QuickBooks — you must manually delete the old one.\n\n` +
      `Proceed?`
    )) return;

    setResyncingId(row.id);
    try {
      const url = `superadmin/billing/invoice/${row.id}/resync-quickbooks${needsForce ? "?force=true" : ""}`;
      const resp = await api.messaging.post(url, {});
      if (resp.ok) {
        const r = await resp.json();
        toastAlert.displayToast(r.success ? Severity.Success : Severity.Error,
          r.success ? "Resynced to QuickBooks." : (r.error ?? "Resync failed."));
        if (r.success) loadQbStatus();
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setResyncingId(null);
    }
  }

  useEffect(() => { loadQbStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = summary?.quickBooks.pendingSyncCount ?? 0;
  const failedCount = summary?.quickBooks.failedSyncCount ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Pending QB Sync"
          value={String(pendingCount)}
          sub="Paid invoices not yet pushed"
          color={pendingCount > 0 ? "orange" : "slate"}
        />
        <StatCard
          label="Failed Sync"
          value={String(failedCount)}
          sub="Sync errors to review"
          color={failedCount > 0 ? "orange" : "slate"}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-sm font-medium text-slate-700 mb-1">Sync Invoices to QuickBooks</p>
        <p className="text-xs text-slate-400 mb-4">
          Pushes up to 100 paid invoices that haven't been synced yet. Skips invoices with a
          previous sync failure — use the differential table below to resync those individually.
        </p>
        <button
          onClick={handleSync}
          disabled={syncing || pendingCount === 0}
          className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
        >
          {syncing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-brands fa-connectdevelop" />}
          Sync Now {pendingCount > 0 && `(${pendingCount} pending)`}
        </button>
      </div>

      {/* QB Differential / Resync */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-sm font-medium text-slate-700 mb-3">QB Sync Differential</p>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>From</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-xs" />
            <span>To</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-xs" />
          </div>
          <button onClick={loadQbStatus} disabled={loadingStatus}
            className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs rounded hover:bg-slate-200 disabled:opacity-50">
            {loadingStatus ? <i className="fa-solid fa-spinner fa-spin" /> : "Load"}
          </button>
        </div>
        {qbStatus && (
          <>
            <div className="flex gap-4 text-xs text-slate-500 mb-3 flex-wrap">
              <span className="text-green-700">✓ {qbStatus.summary.synced} synced</span>
              <span className={qbStatus.summary.pending > 0 ? "text-amber-600" : "text-slate-400"}>
                ⏳ {qbStatus.summary.pending} pending
              </span>
              <span className={qbStatus.summary.failed > 0 ? "text-red-600" : "text-slate-400"}>
                ✕ {qbStatus.summary.failed} failed
              </span>
              <span className="text-slate-400">⊘ {qbStatus.summary.skipped} skipped</span>
              <span className="text-slate-500">Total: {qbStatus.summary.total}</span>
            </div>
            {qbStatus.needsAction.length > 0 ? (
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 text-red-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Invoice #</th>
                      <th className="px-3 py-2 text-left">Site</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Error</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {qbStatus.needsAction.map(row => (
                      <tr key={row.id} className="bg-white">
                        <td className="px-3 py-2 font-mono">{row.invoiceNumber}</td>
                        <td className="px-3 py-2">{row.siteId}</td>
                        <td className="px-3 py-2">{row.invoiceDate?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right">${(row.totalAmount / 100).toFixed(2)}</td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            {row.qbSyncStatus ?? "not synced"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-400 max-w-[200px] truncate">{row.qbSyncError}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleResync(row)}
                            disabled={resyncingId === row.id}
                            className="px-2 py-1 bg-primary text-white rounded text-xs hover:bg-primary-active disabled:opacity-50"
                          >
                            {resyncingId === row.id ? <i className="fa-solid fa-spinner fa-spin" /> : "Resync"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-green-700 bg-green-50 rounded-lg p-3">
                ✓ All paid invoices in this range are synced to QuickBooks.
              </p>
            )}
          </>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <p className="font-medium mb-1">QuickBooks connection</p>
        <p>
          To connect or reconnect QuickBooks, go to <strong>SuperAdmin → Billing → Site Detail</strong>{" "}
          and use the Connect QuickBooks button for the Velaro internal site. The connection is
          per-site and uses OAuth 2.0.
        </p>
      </div>
    </div>
  );
}

// ── Billing Health Warnings ────────────────────────────────────────────────────

interface BillingWarning {
  siteId: number;
  id: number;
  status: string;
  billedAmount: number;
  paymentMethod: string;
  nextRenewalDate: string | null;
  manualBillingMode: boolean;
  hasRecurringItems: boolean;
  warningType: string;
  notes: string | null;
}

function BillingHealthTab() {
  const api = useApi();
  const [warnings, setWarnings] = useState<BillingWarning[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("superadmin/billing/billing-health-warnings");
        if (resp.ok) {
          const data = await resp.json();
          setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        }
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    })();
  }, [api.messaging]);

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Checking billing health…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <i className={`fa-solid fa-triangle-exclamation ${warnings.length > 0 ? "text-amber-500" : "text-green-500"}`} />
        <p className="text-sm font-medium text-slate-700">
          {warnings.length === 0
            ? "All active subscriptions have billing configured."
            : `${warnings.length} active subscription${warnings.length > 1 ? "s" : ""} may be missing billing setup.`}
        </p>
      </div>
      <p className="text-xs text-slate-400">
        Shows active/trialing subscriptions that have had no invoice in the last 60 days and either have
        no billed amount or are in manual billing mode with no recurring items.
        Excludes accounts set to cancel or billing-only mode.
      </p>
      {warnings.length > 0 && (
        <div className="border border-amber-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-amber-50 text-amber-800">
              <tr>
                <th className="px-3 py-2 text-left">Site ID</th>
                <th className="px-3 py-2 text-left">Sub ID</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Billed $</th>
                <th className="px-3 py-2 text-left">Method</th>
                <th className="px-3 py-2 text-left">Next Renewal</th>
                <th className="px-3 py-2 text-left">Issue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {warnings.map(w => (
                <tr key={w.id} className="bg-white hover:bg-amber-50/40">
                  <td className="px-3 py-2 font-mono font-medium">{w.siteId}</td>
                  <td className="px-3 py-2 text-slate-400">#{w.id}</td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{w.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">${w.billedAmount.toFixed(2)}</td>
                  <td className="px-3 py-2">{w.paymentMethod}</td>
                  <td className="px-3 py-2">{w.nextRenewalDate?.slice(0, 10) ?? "—"}</td>
                  <td className="px-3 py-2 text-amber-700">
                    {w.warningType === "no_billing_amount" ? "No billed amount set" :
                     w.warningType === "manual_mode_no_items" ? "Manual mode · no line items" : w.warningType}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── StaxBill Import Tab ───────────────────────────────────────────────────────

const BILLING_INTERVALS = ["monthly", "quarterly", "annual", "biannual", "triennial"];
const PAYMENT_METHODS = ["invoice", "stripe", "ach", "check"];

// ── CSV parser + types ────────────────────────────────────────────────────────

interface CsvRow {
  siteId: number;
  staxbillsAccountId: string;
  planName: string;
  monthlyPrice: number;
  billingInterval: string;
  contractStartDate: string;
  nextRenewalDate: string;
  contractEndDate: string;
  paymentMethod: string;
  parallelBillingMode: boolean;
}

interface PreviewRow {
  rowIndex: number;
  siteId: number;
  companyName: string | null;
  planName: string;
  resolvedPackageId: number | null;
  monthlyPrice: number;
  billingInterval: string;
  contractStartDate: string;
  nextRenewalDate: string;
  contractEndDate: string | null;
  paymentMethod: string;
  staxbillsAccountId: string | null;
  parallelBillingMode: boolean;
  status: "valid" | "site_not_found" | "invalid_plan" | "renewal_past" | "already_imported";
  error: string | null;
}

interface ExecuteResult {
  rowIndex: number;
  siteId: number;
  result: "imported" | "skipped" | "error";
  reason?: string;
  subscriptionId?: number;
}

interface ParallelSite {
  id: number;
  siteId: number;
  companyName: string | null;
  email: string | null;
  packageId: number;
  billedAmount: number;
  baseMonthlyPrice: number;
  billingInterval: string;
  nextRenewalDate: string | null;
  contractEndDate: string | null;
  externalBillingSystem: string | null;
  staxbillsAccountId: string | null;
}

const CSV_TEMPLATE = `site_id,staxbills_account_id,plan_name,monthly_price,billing_interval,contract_start_date,next_renewal_date,contract_end_date,payment_method,parallel_billing_mode
1001,stax_abc123,Professional,249.00,annual,2024-01-01,2026-01-01,2026-01-01,invoice,true
1002,stax_def456,Starter,99.00,monthly,2025-06-01,2026-05-01,,invoice,true`;

function parseCsv(text: string): CsvRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].toLowerCase().split(",").map((h) => h.trim());
  const col = (name: string) => headers.indexOf(name);
  return lines.slice(1).map((line) => {
    const vals = line.split(",").map((v) => v.trim());
    const get = (name: string) => vals[col(name)] ?? "";
    return {
      siteId: parseInt(get("site_id"), 10) || 0,
      staxbillsAccountId: get("staxbills_account_id"),
      planName: get("plan_name"),
      monthlyPrice: parseFloat(get("monthly_price")) || 0,
      billingInterval: get("billing_interval") || "annual",
      contractStartDate: get("contract_start_date"),
      nextRenewalDate: get("next_renewal_date"),
      contractEndDate: get("contract_end_date"),
      paymentMethod: get("payment_method") || "invoice",
      parallelBillingMode: get("parallel_billing_mode").toLowerCase() !== "false",
    };
  });
}

function statusBadge(status: PreviewRow["status"]) {
  const map: Record<string, { label: string; cls: string }> = {
    valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-700" },
    site_not_found: { label: "Site Not Found", cls: "bg-red-100 text-red-700" },
    invalid_plan: { label: "Invalid Plan", cls: "bg-red-100 text-red-700" },
    renewal_past: { label: "Renewal Past", cls: "bg-amber-100 text-amber-700" },
    already_imported: { label: "Already Imported", cls: "bg-slate-100 text-slate-600" },
  };
  const { label, cls } = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

function resultBadge(result: ExecuteResult["result"]) {
  const map: Record<string, { label: string; cls: string }> = {
    imported: { label: "Imported", cls: "bg-emerald-100 text-emerald-700" },
    skipped: { label: "Skipped", cls: "bg-slate-100 text-slate-600" },
    error: { label: "Error", cls: "bg-red-100 text-red-700" },
  };
  const { label, cls } = map[result] ?? { label: result, cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

// ── Parallel sites (cutover queue) ────────────────────────────────────────────

function ParallelSitesList() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [sites, setSites] = useState<ParallelSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [cutovering, setCutovering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/stax-import/parallel-sites");
      if (resp.ok) {
        const data = await resp.json();
        setSites(Array.isArray(data) ? data : []);
      }
    } catch {
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  function toggleSelect(siteId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === sites.length) setSelected(new Set());
    else setSelected(new Set(sites.map((s) => s.siteId)));
  }

  async function handleBulkCutover() {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!confirm(`Cut over ${count} site(s) to full Velaro billing? This enables live invoice delivery and QuickBooks sync.`)) return;
    setCutovering(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/stax-import/bulk-cutover", Array.from(selected));
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(Severity.Success, `${data.cutover} site(s) cut over successfully.`);
        setSelected(new Set());
        load();
      } else {
        toastAlert.displayToast(Severity.Error, "Bulk cutover failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setCutovering(false);
    }
  }

  async function cutoverOne(siteId: number) {
    if (!confirm(`Cut over site ${siteId} to full Velaro billing?`)) return;
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/cutover`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, `Site ${siteId} cut over.`);
        load();
      } else {
        toastAlert.displayToast(Severity.Error, "Cutover failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <i className="fa-solid fa-spinner fa-spin text-slate-400 text-lg" />
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-slate-400">
        No sites in parallel billing mode.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bulk action bar */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{sites.length} site(s) pending cutover</p>
        <div className="flex items-center gap-2">
          <button onClick={toggleAll} className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50">
            {selected.size === sites.length ? "Deselect All" : "Select All"}
          </button>
          <button
            onClick={handleBulkCutover}
            disabled={selected.size === 0 || cutovering}
            className="px-4 py-1.5 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {cutovering && <i className="fa-solid fa-spinner fa-spin" />}
            <i className="fa-solid fa-check-double" />
            Cut Over Selected ({selected.size})
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[32px_1fr_120px_100px_120px_120px_110px] gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
          <span />
          <span>Company / Site</span>
          <span>Plan</span>
          <span>Monthly</span>
          <span>Next Renewal</span>
          <span>Stax ID</span>
          <span className="text-right">Action</span>
        </div>
        <div className="divide-y divide-slate-100">
          {sites.map((s) => (
            <div
              key={s.siteId}
              className={`grid grid-cols-[32px_1fr_120px_100px_120px_120px_110px] gap-2 px-4 py-2.5 items-center text-sm ${
                selected.has(s.siteId) ? "bg-primary-bg" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(s.siteId)}
                onChange={() => toggleSelect(s.siteId)}
                className="rounded border-slate-300 text-primary"
              />
              <div>
                <p className="font-medium text-slate-800">{s.companyName ?? `Site ${s.siteId}`}</p>
                <p className="text-xs text-slate-400">{s.email ?? ""} · #{s.siteId}</p>
              </div>
              <span className="text-slate-600 capitalize">{s.billingInterval}</span>
              <span className="font-medium text-slate-700 tabular-nums">
                ${(s.baseMonthlyPrice ?? 0).toFixed(0)}/mo
              </span>
              <span className="text-slate-500 text-xs">
                {s.nextRenewalDate
                  ? new Date(s.nextRenewalDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                  : "—"}
              </span>
              <span className="text-xs text-slate-400 truncate">{s.staxbillsAccountId ?? "—"}</span>
              <div className="flex justify-end">
                <button
                  onClick={() => cutoverOne(s.siteId)}
                  className="px-3 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                >
                  Cut Over
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Bulk CSV Import ────────────────────────────────────────────────────────────

// Detect if CSV is a native Stax export (has customer_email/email but no site_id)
function isStaxNativeCsv(text: string): boolean {
  const firstLine = text.trim().split(/\r?\n/)[0]?.toLowerCase() ?? "";
  const cols = firstLine.split(",").map((c) => c.trim().replace(/"/g, ""));
  const hasEmail = cols.some((c) => c === "customer_email" || c === "email" || c === "billing_email");
  const hasSiteId = cols.some((c) => c === "site_id");
  return hasEmail && !hasSiteId;
}

// Types for Stax native match results
interface StaxMatchRow {
  rowIndex: number;
  email: string | null;
  customerName: string | null;
  staxCustomerId: string | null;
  staxSubscriptionId: string | null;
  matchStatus: "matched" | "no_match" | "multiple_matches" | "already_imported";
  matchedSiteId: number | null;
  companyName: string | null;
  alreadyImported: boolean;
  monthlyPrice: number;
  billingInterval: string;
  nextRenewalDate: string | null;
  contractStartDate: string | null;
  planName: string | null;
  resolvedPackageId: number | null;
  planResolved: boolean;
  isActive: boolean;
}

function matchStatusBadge(status: StaxMatchRow["matchStatus"]) {
  const map: Record<string, { label: string; cls: string }> = {
    matched: { label: "Matched", cls: "bg-emerald-100 text-emerald-700" },
    no_match: { label: "No Match", cls: "bg-red-100 text-red-700" },
    multiple_matches: { label: "Multiple Sites", cls: "bg-amber-100 text-amber-700" },
    already_imported: { label: "Already Imported", cls: "bg-slate-100 text-slate-600" },
  };
  const { label, cls } = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

function BulkCsvImport() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const fileRef = React.useRef<HTMLInputElement>(null);

  const [csvText, setCsvText] = useState("");
  const [step, setStep] = useState<"input" | "stax-match" | "preview" | "results">("input");
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewStats, setPreviewStats] = useState({ total: 0, valid: 0, invalid: 0 });
  const [selectedRowIndexes, setSelectedRowIndexes] = useState<Set<number>>(new Set());
  const [results, setResults] = useState<ExecuteResult[]>([]);
  const [resultStats, setResultStats] = useState({ imported: 0, skipped: 0, errors: 0 });
  // Stax native match state
  const [staxMatchRows, setStaxMatchRows] = useState<StaxMatchRow[]>([]);
  const [staxMatchStats, setStaxMatchStats] = useState({ matched: 0, noMatch: 0, multipleMatches: 0, alreadyImported: 0 });
  const [selectedMatchIndexes, setSelectedMatchIndexes] = useState<Set<number>>(new Set());

  const csvMode = isStaxNativeCsv(csvText);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText((ev.target?.result as string) ?? "");
    reader.readAsText(file);
  }

  // For Stax native CSVs: match emails → sites, then show confirmation table
  async function handleStaxMatch() {
    setPreviewing(true);
    try {
      const resp = await api.messaging.post("superadmin/billing/stax-import/match-csv", { CsvText: csvText });
      if (resp.ok) {
        const data = await resp.json();
        setStaxMatchRows(Array.isArray(data.rows) ? data.rows : []);
        setStaxMatchStats({
          matched: data.matched ?? 0,
          noMatch: data.noMatch ?? 0,
          multipleMatches: data.multipleMatches ?? 0,
          alreadyImported: data.alreadyImported ?? 0,
        });
        // Pre-select all matched+active+plan-resolved rows
        const autoSelect = new Set<number>(
          (data.rows as StaxMatchRow[])
            .filter((r) => r.matchStatus === "matched" && r.isActive && r.planResolved)
            .map((r) => r.rowIndex)
        );
        setSelectedMatchIndexes(autoSelect);
        setStep("stax-match");
      } else {
        toastAlert.displayToast(Severity.Error, "Email matching failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setPreviewing(false);
    }
  }

  // Convert matched Stax rows → Velaro import rows → preview
  async function handleStaxMatchToPreview() {
    const selected = staxMatchRows.filter((r) => selectedMatchIndexes.has(r.rowIndex));
    if (selected.length === 0) { toastAlert.displayToast(Severity.Warning, "No rows selected."); return; }
    setPreviewing(true);
    try {
      const payload = selected.map((r) => ({
        SiteId: r.matchedSiteId!,
        StaxbillsAccountId: r.staxCustomerId || r.staxSubscriptionId || null,
        PlanName: r.planName || "Professional",
        MonthlyPrice: r.monthlyPrice,
        BillingInterval: r.billingInterval,
        ContractStartDate: r.contractStartDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        NextRenewalDate: r.nextRenewalDate || new Date().toISOString(),
        ContractEndDate: null,
        PaymentMethod: "invoice",
        ParallelBillingMode: true,
      }));
      const resp = await api.messaging.post("superadmin/billing/stax-import/preview", payload);
      if (resp.ok) {
        const data = await resp.json();
        setPreviewRows(Array.isArray(data.rows) ? data.rows : []);
        setPreviewStats({ total: data.total ?? 0, valid: data.valid ?? 0, invalid: data.invalid ?? 0 });
        const validIndexes = new Set<number>(
          (data.rows as PreviewRow[]).filter((r) => r.status === "valid").map((r) => r.rowIndex)
        );
        setSelectedRowIndexes(validIndexes);
        setStep("preview");
      } else {
        toastAlert.displayToast(Severity.Error, "Preview failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setPreviewing(false);
    }
  }

  async function handlePreview() {
    const rows = parseCsv(csvText);
    if (rows.length === 0) {
      toastAlert.displayToast(Severity.Warning, "No rows found. Check CSV format.");
      return;
    }
    setPreviewing(true);
    try {
      const payload = rows.map((r) => ({
        SiteId: r.siteId,
        StaxbillsAccountId: r.staxbillsAccountId || null,
        PlanName: r.planName,
        MonthlyPrice: r.monthlyPrice,
        BillingInterval: r.billingInterval,
        ContractStartDate: r.contractStartDate,
        NextRenewalDate: r.nextRenewalDate,
        ContractEndDate: r.contractEndDate || null,
        PaymentMethod: r.paymentMethod,
        ParallelBillingMode: r.parallelBillingMode,
      }));
      const resp = await api.messaging.post("superadmin/billing/stax-import/preview", payload);
      if (resp.ok) {
        const data = await resp.json();
        setPreviewRows(Array.isArray(data.rows) ? data.rows : []);
        setPreviewStats({ total: data.total ?? 0, valid: data.valid ?? 0, invalid: data.invalid ?? 0 });
        // Pre-select all valid rows
        const validIndexes = (data.rows as PreviewRow[]).filter((r) => r.status === "valid").map((r) => r.rowIndex);
        setSelectedRowIndexes(new Set(validIndexes));
        setStep("preview");
      } else {
        toastAlert.displayToast(Severity.Error, "Preview failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setPreviewing(false);
    }
  }

  function toggleRowSelect(idx: number) {
    setSelectedRowIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }

  function toggleAllValid() {
    const validIndexes = previewRows.filter((r) => r.status === "valid").map((r) => r.rowIndex);
    if (validIndexes.every((i) => selectedRowIndexes.has(i))) {
      setSelectedRowIndexes(new Set());
    } else {
      setSelectedRowIndexes(new Set(validIndexes));
    }
  }

  async function handleExecute() {
    const selectedRows = previewRows.filter((r) => selectedRowIndexes.has(r.rowIndex));
    if (selectedRows.length === 0) {
      toastAlert.displayToast(Severity.Warning, "No rows selected.");
      return;
    }
    if (!confirm(`Import ${selectedRows.length} account(s) from Stax to Velaro billing? This cannot be undone.`)) return;
    setExecuting(true);
    try {
      const payload = selectedRows.map((r) => ({
        SiteId: r.siteId,
        StaxbillsAccountId: r.staxbillsAccountId ?? null,
        PlanName: r.planName,
        MonthlyPrice: r.monthlyPrice,
        BillingInterval: r.billingInterval,
        ContractStartDate: r.contractStartDate,
        NextRenewalDate: r.nextRenewalDate,
        ContractEndDate: r.contractEndDate ?? null,
        PaymentMethod: r.paymentMethod,
        ParallelBillingMode: r.parallelBillingMode,
      }));
      const resp = await api.messaging.post("superadmin/billing/stax-import/execute", payload);
      if (resp.ok) {
        const data = await resp.json();
        setResults(Array.isArray(data.rows) ? data.rows : []);
        setResultStats({
          imported: data.imported ?? 0,
          skipped: data.skipped ?? 0,
          errors: data.errors ?? 0,
        });
        setStep("results");
      } else {
        toastAlert.displayToast(Severity.Error, "Import failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setExecuting(false);
    }
  }

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stax-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Step 1: CSV Input ──────────────────────────────────────────────────────

  if (step === "input") {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Bulk CSV Import</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Upload or paste a CSV of Stax accounts to migrate. All imported accounts start in
              parallel billing mode until you cut them over.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
          >
            <i className="fa-solid fa-download text-xs" />
            Download Template
          </button>
        </div>

        {/* File upload */}
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-primary-hover hover:bg-primary-bg transition-colors"
        >
          <i className="fa-solid fa-file-csv text-2xl text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Click to upload a CSV file</p>
          <p className="text-xs text-slate-400 mt-0.5">or paste CSV text below</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
        </div>

        {/* Paste area */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">CSV Text</label>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={8}
            spellCheck={false}
            placeholder={CSV_TEMPLATE}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary resize-y"
          />
          {csvText && (
            <div className="flex items-center gap-3 mt-1">
              {csvMode ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                  <i className="fa-solid fa-wand-magic-sparkles" />
                  Stax native export detected — will match accounts by email
                </span>
              ) : (
                <span className="text-xs text-slate-400">
                  {parseCsv(csvText).length} row(s) detected (Velaro format)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Column reference — show the right format hint */}
        {csvMode ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 space-y-1">
            <p className="font-medium mb-1">Stax native export detected</p>
            <p>Accounts will be matched to Velaro sites by email address. Supported Stax columns:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 mt-1 text-blue-700">
              {[
                ["customer_email / email", "Customer email (required for matching)"],
                ["amount", "Monthly charge in dollars"],
                ["frequency", "Monthly | Annually | Quarterly"],
                ["next_bill_date", "Next billing date"],
                ["customer_id", "Stax customer ID (stored for reference)"],
                ["subscription_id / plan_id", "Stax subscription ID"],
                ["plan_name / description", "Used to resolve Velaro package"],
                ["status", "Active rows only — inactive rows flagged"],
              ].map(([col, desc]) => (
                <div key={col} className="flex gap-1">
                  <code className="text-blue-600 shrink-0">{col}</code>
                  <span className="text-blue-500">— {desc}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-500 space-y-1">
            <p className="font-medium text-slate-600 mb-1">Velaro format — required columns:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {[
                ["site_id", "Velaro site ID (integer)"],
                ["plan_name", "Starter | Professional | Enterprise"],
                ["monthly_price", "Base monthly price (decimal)"],
                ["billing_interval", "annual | monthly | quarterly"],
                ["contract_start_date", "YYYY-MM-DD"],
                ["next_renewal_date", "YYYY-MM-DD"],
                ["staxbills_account_id", "Optional — Stax reference ID"],
                ["contract_end_date", "Optional — YYYY-MM-DD"],
                ["payment_method", "invoice | stripe | ach | check"],
                ["parallel_billing_mode", "true (default) | false"],
              ].map(([col, desc]) => (
                <div key={col} className="flex gap-1">
                  <code className="text-primary shrink-0">{col}</code>
                  <span className="text-slate-400">— {desc}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-400 mt-1">
              Don't have site IDs? Use a Stax native export (with <code>customer_email</code> column) instead — we'll match by email automatically.
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={csvMode ? handleStaxMatch : handlePreview}
            disabled={previewing || !csvText.trim()}
            className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
          >
            {previewing ? (
              <i className="fa-solid fa-spinner fa-spin" />
            ) : csvMode ? (
              <i className="fa-solid fa-magnifying-glass" />
            ) : (
              <i className="fa-solid fa-table" />
            )}
            {csvMode ? "Match Accounts by Email" : "Preview & Validate"}
          </button>
        </div>
      </div>
    );
  }

  // ── Stax match step ────────────────────────────────────────────────────────

  if (step === "stax-match") {
    const matchable = staxMatchRows.filter((r) => r.matchStatus === "matched" && r.isActive);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <StatCard label="Matched" value={String(staxMatchStats.matched)} color="emerald" sub="Email found in Velaro" />
          <StatCard label="No Match" value={String(staxMatchStats.noMatch)} color="orange" sub="Email not in Velaro" />
          <StatCard label="Multiple Sites" value={String(staxMatchStats.multipleMatches)} color="orange" sub="Ambiguous — manual review" />
          <StatCard label="Already Done" value={String(staxMatchStats.alreadyImported)} color="slate" sub="Staxbill sub exists" />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
            <p className="text-sm font-medium text-slate-700">Email Match Results — {staxMatchRows.length} row(s)</p>
            <button
              onClick={() => {
                const auto = new Set<number>(matchable.filter((r) => r.planResolved).map((r) => r.rowIndex));
                setSelectedMatchIndexes(selectedMatchIndexes.size === auto.size ? new Set() : auto);
              }}
              className="text-xs text-primary hover:underline"
            >
              {selectedMatchIndexes.size === matchable.filter((r) => r.planResolved).length ? "Deselect All" : "Select All Matched"}
            </button>
          </div>
          <div className="grid grid-cols-[32px_1fr_1fr_120px_120px_120px] gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
            <span />
            <span>Customer Email</span>
            <span>Velaro Site</span>
            <span>Amount</span>
            <span>Frequency</span>
            <span>Status</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {staxMatchRows.map((row) => {
              const canSelect = row.matchStatus === "matched" && row.isActive;
              return (
                <div key={row.rowIndex} className={`grid grid-cols-[32px_1fr_1fr_120px_120px_120px] gap-2 px-4 py-2.5 items-start text-sm ${selectedMatchIndexes.has(row.rowIndex) ? "bg-primary-bg" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selectedMatchIndexes.has(row.rowIndex)}
                    disabled={!canSelect}
                    onChange={() => {
                      setSelectedMatchIndexes((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.rowIndex)) next.delete(row.rowIndex); else next.add(row.rowIndex);
                        return next;
                      });
                    }}
                    className="mt-0.5 rounded border-slate-300 text-primary disabled:opacity-30"
                  />
                  <div>
                    <p className="font-medium text-slate-800">{row.email}</p>
                    {row.customerName && <p className="text-xs text-slate-400">{row.customerName}</p>}
                    {row.staxCustomerId && <p className="text-xs text-slate-300">Stax: {row.staxCustomerId}</p>}
                  </div>
                  <div>
                    {row.matchedSiteId ? (
                      <>
                        <p className="font-medium text-slate-800">{row.companyName ?? `Site ${row.matchedSiteId}`}</p>
                        <p className="text-xs text-slate-400">#{row.matchedSiteId}</p>
                      </>
                    ) : (
                      <p className="text-xs text-slate-400 italic">—</p>
                    )}
                  </div>
                  <span className="text-slate-700 tabular-nums">${row.monthlyPrice.toFixed(2)}/mo</span>
                  <span className="text-slate-600 capitalize">{row.billingInterval}</span>
                  <div className="space-y-0.5">
                    {matchStatusBadge(row.matchStatus)}
                    {!row.planResolved && row.matchStatus === "matched" && (
                      <p className="text-xs text-amber-600">Plan "{row.planName}" not found</p>
                    )}
                    {!row.isActive && <p className="text-xs text-slate-400">Inactive in Stax</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {staxMatchStats.noMatch > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            <p className="font-medium mb-1">
              <i className="fa-solid fa-triangle-exclamation mr-1" />
              {staxMatchStats.noMatch} account(s) couldn't be matched
            </p>
            <p>These customers exist in Stax but have no Velaro account with that email. Options: (1) create their Velaro account first, or (2) use the Single Account import with their site ID directly.</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button onClick={() => setStep("input")} className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
            <i className="fa-solid fa-arrow-left mr-1.5" />Back
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{selectedMatchIndexes.size} account(s) selected</span>
            <button
              onClick={handleStaxMatchToPreview}
              disabled={previewing || selectedMatchIndexes.size === 0}
              className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
            >
              {previewing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-arrow-right" />}
              Continue to Preview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Preview ────────────────────────────────────────────────────────

  if (step === "preview") {
    return (
      <div className="space-y-4">
        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Rows" value={String(previewStats.total)} color="slate" />
          <StatCard label="Valid" value={String(previewStats.valid)} color="emerald" />
          <StatCard label="Invalid / Skipped" value={String(previewStats.invalid)} color={previewStats.invalid > 0 ? "orange" : "slate"} />
        </div>

        {/* Row table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
            <p className="text-sm font-medium text-slate-700">Preview — {previewRows.length} row(s)</p>
            <button onClick={toggleAllValid} className="text-xs text-primary hover:underline">
              {previewRows.filter((r) => r.status === "valid").every((r) => selectedRowIndexes.has(r.rowIndex))
                ? "Deselect All Valid"
                : "Select All Valid"}
            </button>
          </div>
          <div className="grid grid-cols-[32px_1fr_120px_100px_120px_120px_1fr] gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
            <span />
            <span>Company / Site</span>
            <span>Plan</span>
            <span>Monthly</span>
            <span>Renewal</span>
            <span>Parallel</span>
            <span>Status</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {previewRows.map((row) => (
              <div
                key={row.rowIndex}
                className={`grid grid-cols-[32px_1fr_120px_100px_120px_120px_1fr] gap-2 px-4 py-2.5 items-start text-sm ${
                  selectedRowIndexes.has(row.rowIndex) ? "bg-primary-bg" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedRowIndexes.has(row.rowIndex)}
                  disabled={row.status !== "valid"}
                  onChange={() => toggleRowSelect(row.rowIndex)}
                  className="mt-0.5 rounded border-slate-300 text-primary disabled:opacity-30"
                />
                <div>
                  <p className="font-medium text-slate-800">{row.companyName ?? `Site ${row.siteId}`}</p>
                  <p className="text-xs text-slate-400">#{row.siteId}</p>
                </div>
                <span className="text-slate-600">{row.planName}</span>
                <span className="text-slate-700 tabular-nums">${row.monthlyPrice}/mo</span>
                <span className="text-xs text-slate-500">
                  {new Date(row.nextRenewalDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                </span>
                <span className={`text-xs font-medium ${row.parallelBillingMode ? "text-amber-600" : "text-emerald-600"}`}>
                  {row.parallelBillingMode ? "Parallel" : "Live"}
                </span>
                <div>
                  {statusBadge(row.status)}
                  {row.error && <p className="text-xs text-red-500 mt-0.5">{row.error}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setStep("input")}
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50"
          >
            <i className="fa-solid fa-arrow-left mr-1.5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{selectedRowIndexes.size} row(s) selected</span>
            <button
              onClick={handleExecute}
              disabled={executing || selectedRowIndexes.size === 0}
              className="px-5 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              {executing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-file-import" />}
              Import {selectedRowIndexes.size} Account(s)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 3: Results ────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Imported" value={String(resultStats.imported)} color="emerald" />
        <StatCard label="Skipped" value={String(resultStats.skipped)} color="slate" />
        <StatCard label="Errors" value={String(resultStats.errors)} color={resultStats.errors > 0 ? "orange" : "slate"} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">Import Results</p>
        </div>
        <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {results.map((r) => (
            <div key={r.rowIndex} className="flex items-center gap-3 px-4 py-2.5">
              {resultBadge(r.result)}
              <span className="text-sm text-slate-700">Site {r.siteId}</span>
              {r.subscriptionId && (
                <span className="text-xs text-slate-400">→ Subscription #{r.subscriptionId}</span>
              )}
              {r.reason && <span className="text-xs text-slate-400">({r.reason})</span>}
            </div>
          ))}
        </div>
      </div>

      {resultStats.imported > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <p className="font-medium mb-1">
            <i className="fa-solid fa-triangle-exclamation mr-1.5" />
            {resultStats.imported} account(s) imported in parallel billing mode
          </p>
          <p className="text-xs">
            Invoices are generating but email and QuickBooks sync are suppressed.
            Switch to the <strong>Cutover Queue</strong> tab below to verify and go live.
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => { setStep("input"); setCsvText(""); setResults([]); setPreviewRows([]); }}
          className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50"
        >
          Import Another Batch
        </button>
      </div>
    </div>
  );
}

// ── Migration Tracker ─────────────────────────────────────────────────────────

interface TrackerRow {
  id: number;
  siteId: number;
  companyName: string | null;
  email: string | null;
  pipelineState: "imported" | "parallel_invoiced" | "verified" | "live";
  packageName: string | null;
  billingInterval: string;
  baseMonthlyPrice: number;
  billedAmount: number | null;
  nextRenewalDate: string | null;
  contractEndDate: string | null;
  staxbillsAccountId: string | null;
  parallelBillingMode: boolean;
  billingCutoverDate: string | null;
  parallelInvoiceCount: number;
  lastParallelInvoiceDate: string | null;
  lastParallelInvoiceAmount: number | null;
  amountDiff: number | null;
  amountMatch: boolean | null;
  importedAt: string | null;
  importedBy: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  cutoverAt: string | null;
  cutoverBy: string | null;
}

interface SimulateResult {
  siteId: number;
  companyName: string | null;
  billingInterval: string;
  baseMonthlyPrice: number;
  projectedAmount: number;
  lineItemBreakdown: string;
  nextPeriodStart: string;
  nextPeriodEnd: string;
  nextRenewalDate: string | null;
  staxRecordedAmount: number | null;
  status: string;
}

interface ProjectedLineItem {
  description: string;
  quantity: number;
  unitAmountCents: number;
  totalCents: number;
  type: string;
  isUpliftProjection: boolean;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
}

interface ProjectedInvoice {
  siteId: number;
  siteName: string | null;
  contact: {
    name: string | null;
    email: string | null;
    phone: string | null;
    role: string | null;
  } | null;
  periodStart: string;
  periodEnd: string;
  invoiceDate: string;
  dueDate: string;
  billingInterval: string;
  paymentTermsDays: number;
  currency: string;
  lineItems: ProjectedLineItem[];
  totalCents: number;
  totalAmount: number;
  crossesContractAnniversary: boolean;
  contractAnniversaryDate: string | null;
  upliftPercent: number | null;
  isParallelMode: boolean;
  isBillingOnlyMode: boolean;
}

// ── Projected Invoice Modal ────────────────────────────────────────────────────

const DOCUMENT_TITLES = [
  "Projected Invoice",
  "Quote",
  "Renewal Preview",
  "Renewal Estimate",
  "Price Proposal",
];

const UPLIFT_INTERVALS = [
  { value: "monthly",    label: "Every month" },
  { value: "quarterly",  label: "Every quarter" },
  { value: "annual",     label: "Every year" },
  { value: "biannual",   label: "Every 2 years" },
  { value: "triennial",  label: "Every 3 years" },
];

function ProjectedInvoiceModal({ siteId, onClose }: { siteId: number; onClose: () => void }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [data, setData] = useState<ProjectedInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Title + sharing
  const [docTitle, setDocTitle] = useState("Projected Invoice");
  const [customTitle, setCustomTitle] = useState("");
  const [sharing, setSharing] = useState(false);
  const [sharedToken, setSharedToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sendResult, setSendResult] = useState<string | null>(null);

  const effectiveTitle = docTitle === "__custom__" ? customTitle : docTitle;

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.messaging.get(`superadmin/billing/site/${siteId}/project-invoice`);
        if (resp.ok) {
          setData(await resp.json());
        } else {
          setError("Failed to load projected invoice.");
        }
      } catch {
        setError("Network error.");
      } finally {
        setLoading(false);
      }
    })();
  }, [api.messaging, siteId]);

  async function handleShare() {
    setSharing(true);
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/share-document`, {
        Title: effectiveTitle,
        DocumentType: docTitle === "Quote" ? "quote" : "projected",
      });
      if (resp.ok) {
        const result = await resp.json();
        setSharedToken(result.token);
        toastAlert.displayToast(Severity.Success, "Shareable link generated.");
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to generate link.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSharing(false);
    }
  }

  async function handleSend() {
    if (!sharedToken) { toastAlert.displayToast(Severity.Warning, "Generate a link first."); return; }
    setSending(true);
    setSendResult(null);
    try {
      const resp = await api.messaging.post(
        `superadmin/billing/site/${siteId}/send-document/${sharedToken}`,
        {
          ToEmail: sendEmail || undefined,
          CustomMessage: sendMessage || undefined,
        }
      );
      if (resp.ok) {
        const result = await resp.json();
        setSendResult(`Sent to ${result.sentTo}`);
        toastAlert.displayToast(Severity.Success, `Document emailed to ${result.sentTo}`);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to send email.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSending(false);
    }
  }

  function handlePrint() {
    const win = window.open("", "_blank");
    if (!win || !data) return;
    const lines = data.lineItems.map((li) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${li.description}${li.isUpliftProjection ? ' <span style="font-size:11px;color:#7c3aed">▲ uplift</span>' : ''}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:center">${li.quantity}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right">$${(li.unitAmountCents / 100).toFixed(2)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:500">$${(li.totalCents / 100).toFixed(2)}</td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>${effectiveTitle} — Site ${siteId}</title>
      <style>body{font-family:sans-serif;max-width:760px;margin:40px auto;color:#1e293b}
      table{width:100%;border-collapse:collapse}th{background:#f8fafc;padding:8px;text-align:left;font-size:12px;text-transform:uppercase;color:#64748b}
      </style></head><body>
      <h2 style="margin:0 0 4px">${effectiveTitle.toUpperCase()}</h2>
      <p style="color:#64748b;font-size:13px;margin:0 0 24px">This is a preview of the next renewal. No payment is due at this time.</p>
      <div style="display:flex;gap:40px;margin-bottom:24px">
        <div><strong>Bill To:</strong><br>${data.contact?.name ?? "—"}<br>${data.contact?.email ?? ""}<br>${data.contact?.phone ?? ""}</div>
        <div><strong>Invoice Date:</strong> ${new Date(data.invoiceDate).toLocaleDateString()}<br>
          <strong>Due Date:</strong> ${new Date(data.dueDate).toLocaleDateString()}<br>
          <strong>Period:</strong> ${new Date(data.periodStart).toLocaleDateString()} – ${new Date(data.periodEnd).toLocaleDateString()}</div>
      </div>
      ${data.crossesContractAnniversary ? '<p style="background:#f3e8ff;border:1px solid #a855f7;border-radius:6px;padding:8px 12px;font-size:12px;color:#7c3aed">&#9650; Contract anniversary falls in this period — uplift of ' + data.upliftPercent + '% is projected.</p>' : ""}
      <table><thead><tr><th>Description</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>${lines}</tbody>
      <tfoot><tr><td colspan="3" style="padding:10px 8px;font-weight:700;text-align:right;font-size:16px">Total Due:</td>
        <td style="padding:10px 8px;font-weight:700;font-size:16px;text-align:right">$${data.totalAmount.toFixed(2)} ${data.currency.toUpperCase()}</td></tr></tfoot></table>
      <p style="margin-top:32px;font-size:11px;color:#94a3b8">Net ${data.paymentTermsDays} — Velaro Inc. · billing@velaro.com</p>
      </body></html>`);
    win.document.close();
    win.print();
  }

  const fmtDate = (d: string) => new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const fmtAmt = (cents: number) => "$" + (cents / 100).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4 gap-4">
          <div className="flex-1 min-w-0">
            {/* Editable document title */}
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                className="text-sm font-semibold text-slate-800 bg-transparent border-none outline-none cursor-pointer hover:text-primary pr-6"
              >
                {DOCUMENT_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__custom__">Custom title…</option>
              </select>
              {docTitle === "__custom__" && (
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Enter title"
                  className="text-sm font-semibold border-b border-slate-300 outline-none px-1 text-slate-800"
                />
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Site {siteId} — next renewal preview</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {data && (
              <button
                onClick={handlePrint}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"
              >
                <i className="fa-solid fa-print" />
                Print / PDF
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {data && !loading && (
            <div className="space-y-5">
              {/* Warnings */}
              {data.isParallelMode && (
                <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                  <i className="fa-solid fa-triangle-exclamation mr-1" />
                  Parallel billing mode is active — this invoice will be generated but email delivery is suppressed.
                </div>
              )}
              {data.isBillingOnlyMode && (
                <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-xs text-sky-700">
                  <i className="fa-solid fa-circle-info mr-1" />
                  Billing-only mode — payment collected but feature gating not enforced.
                </div>
              )}
              {data.crossesContractAnniversary && (
                <div className="bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-xs text-violet-700">
                  <i className="fa-solid fa-circle-arrow-up mr-1" />
                  Contract anniversary on {fmtDate(data.contractAnniversaryDate!)} — projected uplift of{" "}
                  <strong>{data.upliftPercent}%</strong> applied to marked lines.
                </div>
              )}

              {/* Period + due date */}
              <div className="grid grid-cols-3 gap-3 text-xs text-slate-600 bg-slate-50 rounded-lg px-4 py-3">
                <div><span className="text-slate-400 block">Invoice Date</span>{fmtDate(data.invoiceDate)}</div>
                <div><span className="text-slate-400 block">Period</span>{fmtDate(data.periodStart)} – {fmtDate(data.periodEnd)}</div>
                <div><span className="text-slate-400 block">Due Date (Net {data.paymentTermsDays})</span>{fmtDate(data.dueDate)}</div>
              </div>

              {/* Bill-to */}
              {data.contact && (
                <div className="text-xs text-slate-600">
                  <p className="font-medium text-slate-700 mb-0.5">Bill To</p>
                  {data.contact.name && <p>{data.contact.name}</p>}
                  {data.contact.role && <p className="text-slate-400">{data.contact.role}</p>}
                  {data.contact.email && <p>{data.contact.email}</p>}
                  {data.contact.phone && <p>{data.contact.phone}</p>}
                </div>
              )}

              {/* Line items */}
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 text-slate-500 font-medium">Description</th>
                    <th className="text-center px-3 py-2 text-slate-500 font-medium w-12">Qty</th>
                    <th className="text-right px-3 py-2 text-slate-500 font-medium w-24">Unit</th>
                    <th className="text-right px-3 py-2 text-slate-500 font-medium w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lineItems.map((li, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-700">
                        {li.description}
                        {li.isUpliftProjection && (
                          <span className="ml-1.5 text-violet-600 text-[10px] font-medium">▲ uplift projected</span>
                        )}
                        {li.effectiveUntil && (
                          <span className="ml-1.5 text-amber-600 text-[10px]">expires {fmtDate(li.effectiveUntil)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-600">{li.quantity}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmtAmt(li.unitAmountCents)}</td>
                      <td className={`px-3 py-2 text-right font-medium ${li.totalCents < 0 ? "text-emerald-600" : "text-slate-800"}`}>
                        {fmtAmt(li.totalCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200">
                    <td colSpan={3} className="px-3 py-2.5 text-right font-semibold text-slate-700">Total Due</td>
                    <td className="px-3 py-2.5 text-right font-bold text-slate-900 text-sm">
                      ${data.totalAmount.toFixed(2)} {data.currency.toUpperCase()}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <p className="text-[10px] text-slate-400">
                Net {data.paymentTermsDays} · Velaro Inc. · billing@velaro.com
                {data.crossesContractAnniversary && " · Amounts marked 'uplift projected' reflect the scheduled price increase at contract anniversary and will be applied automatically."}
              </p>

              {/* ── Share / Send panel ─────────────────────────────────────── */}
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <p className="text-xs font-medium text-slate-700">Share with Customer</p>
                <p className="text-[11px] text-slate-400">
                  By default this document is only visible to superadmins. Generate a link to let the
                  customer view it, or send it directly to their billing contact.
                </p>

                {!sharedToken ? (
                  <button
                    onClick={handleShare}
                    disabled={sharing}
                    className="px-4 py-2 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
                  >
                    {sharing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-link" />}
                    Generate Shareable Link
                  </button>
                ) : (
                  <div className="space-y-3">
                    {/* Copy link */}
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={`${window.location.origin.replace("admin", "messaging")}/billing/document/${sharedToken}`}
                        className="flex-1 text-xs rounded border border-slate-300 px-2 py-1.5 bg-slate-50 font-mono"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin.replace("admin", "messaging")}/billing/document/${sharedToken}`);
                          toastAlert.displayToast(Severity.Success, "Link copied!");
                        }}
                        className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50"
                      >
                        <i className="fa-regular fa-copy" />
                      </button>
                    </div>

                    {/* Send email */}
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                      <p className="text-[11px] font-medium text-slate-600">Email to Customer</p>
                      <input
                        type="email"
                        value={sendEmail}
                        onChange={(e) => setSendEmail(e.target.value)}
                        placeholder="Billing contact email (leave blank to use primary contact)"
                        className="w-full text-xs rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-hover"
                      />
                      <textarea
                        value={sendMessage}
                        onChange={(e) => setSendMessage(e.target.value)}
                        placeholder="Optional custom message…"
                        rows={2}
                        className="w-full text-xs rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-hover resize-none"
                      />
                      <div className="flex items-center justify-between">
                        {sendResult && <p className="text-[11px] text-emerald-600"><i className="fa-solid fa-check mr-1" />{sendResult}</p>}
                        <button
                          onClick={handleSend}
                          disabled={sending}
                          className="ml-auto px-4 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {sending ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
                          Send {effectiveTitle}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PIPELINE_CONFIG: Record<string, { label: string; cls: string; icon: string; description: string }> = {
  imported:         { label: "Imported",          cls: "bg-amber-100 text-amber-700",      icon: "fa-file-import",    description: "In parallel mode — no invoices generated yet" },
  parallel_invoiced:{ label: "Invoice Generated", cls: "bg-blue-100 text-blue-700",        icon: "fa-file-invoice",   description: "Parallel invoice fired — compare amounts before going live" },
  verified:         { label: "Verified",           cls: "bg-violet-100 text-violet-700",    icon: "fa-check-circle",   description: "Amounts confirmed by superadmin — ready to cut over" },
  live:             { label: "Live",               cls: "bg-emerald-100 text-emerald-700",  icon: "fa-circle-check",   description: "Billing live on Velaro" },
};

function pipelineBadge(state: string) {
  const cfg = PIPELINE_CONFIG[state] ?? { label: state, cls: "bg-slate-100 text-slate-600", icon: "fa-circle" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      <i className={`fa-solid ${cfg.icon} text-xs`} />
      {cfg.label}
    </span>
  );
}

function MigrationTracker() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [rows, setRows] = useState<TrackerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterState, setFilterState] = useState<string>("all");
  const [simulating, setSimulating] = useState<number | null>(null);
  const [simResult, setSimResult] = useState<Record<number, SimulateResult>>({});
  const [verifying, setVerifying] = useState<number | null>(null);
  const [expandedSite, setExpandedSite] = useState<number | null>(null);
  const [previewSite, setPreviewSite] = useState<number | null>(null);
  const [cancelSite, setCancelSite] = useState<number | null>(null);
  const [bulkDraftSite, setBulkDraftSite] = useState<number | null>(null);
  const [bulkDraftCount, setBulkDraftCount] = useState(6);
  const [bulkDraftDate, setBulkDraftDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bulkDraftSubmitting, setBulkDraftSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/stax-import/migration-tracker");
      if (resp.ok) setRows(Array.isArray(await resp.clone().json()) ? await resp.json() : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => { load(); }, [load]);

  async function handleSimulate(siteId: number) {
    setSimulating(siteId);
    try {
      const resp = await api.messaging.post("superadmin/billing/stax-import/simulate", [siteId]);
      if (resp.ok) {
        const data = await resp.json();
        const result = Array.isArray(data) ? data[0] : null;
        if (result) setSimResult((prev) => ({ ...prev, [siteId]: result }));
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Simulation failed.");
    } finally {
      setSimulating(null);
    }
  }

  async function handleVerify(row: TrackerRow) {
    const sim = simResult[row.siteId];
    const notes = `Stax amount: $${(row.billedAmount ?? 0).toFixed(2)} | Velaro projected: $${(sim?.projectedAmount ?? row.billedAmount ?? 0).toFixed(2)}`;
    setVerifying(row.siteId);
    try {
      const resp = await api.messaging.post(`superadmin/billing/stax-import/${row.siteId}/verify`, {
        StaxAmount: row.billedAmount,
        VelaroAmount: sim?.projectedAmount ?? row.lastParallelInvoiceAmount,
        Notes: notes,
      });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, `Site ${row.siteId} marked verified.`);
        load();
      } else {
        toastAlert.displayToast(Severity.Error, "Verify failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setVerifying(null);
    }
  }

  async function handleCutover(siteId: number) {
    if (!confirm(`Cut over site ${siteId} to full live billing? This cannot be undone.`)) return;
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/cutover`, {});
      if (resp.ok) { toastAlert.displayToast(Severity.Success, `Site ${siteId} is now live.`); load(); }
      else toastAlert.displayToast(Severity.Error, "Cutover failed.");
    } catch { toastAlert.displayToast(Severity.Error, "Network error."); }
  }

  const filtered = filterState === "all" ? rows : rows.filter((r) => r.pipelineState === filterState);

  const counts: Record<string, number> = {};
  rows.forEach((r) => { counts[r.pipelineState] = (counts[r.pipelineState] ?? 0) + 1; });

  if (loading) {
    return <div className="flex items-center justify-center py-12"><i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Pipeline summary */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(PIPELINE_CONFIG).map(([state, cfg]) => (
          <button
            key={state}
            onClick={() => setFilterState(filterState === state ? "all" : state)}
            className={`text-left p-3 rounded-xl border transition ${
              filterState === state ? "border-primary-hover bg-primary-bg" : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${cfg.cls}`}>
                {counts[state] ?? 0}
              </span>
              <span className="text-xs font-medium text-slate-700">{cfg.label}</span>
            </div>
            <p className="text-xs text-slate-400 leading-tight">{cfg.description}</p>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-400">
          No Stax accounts found. Import accounts using <strong>Bulk CSV Import</strong> first.
        </div>
      ) : (
        <>
          {/* Filter indicator */}
          {filterState !== "all" && (
            <div className="flex items-center gap-2">
              {pipelineBadge(filterState)}
              <span className="text-xs text-slate-400">— showing {filtered.length} account(s)</span>
              <button onClick={() => setFilterState("all")} className="text-xs text-primary hover:underline">
                Show all
              </button>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[1fr_120px_120px_100px_160px_80px] gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
              <span>Account</span>
              <span>Plan / Interval</span>
              <span>Stax Amount</span>
              <span>Invoices</span>
              <span>Pipeline State</span>
              <span className="text-right">Actions</span>
            </div>

            {filtered.length === 0 && (
              <p className="text-sm text-center text-slate-400 py-8">No accounts in this stage.</p>
            )}

            <div className="divide-y divide-slate-100">
              {filtered.map((row) => {
                const sim = simResult[row.siteId];
                const isExpanded = expandedSite === row.siteId;

                return (
                  <React.Fragment key={row.siteId}>
                    <div
                      className={`grid grid-cols-[1fr_120px_120px_100px_160px_80px] gap-2 px-4 py-3 items-center text-sm cursor-pointer hover:bg-slate-50 ${isExpanded ? "bg-slate-50" : ""}`}
                      onClick={() => setExpandedSite(isExpanded ? null : row.siteId)}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{row.companyName ?? `Site ${row.siteId}`}</p>
                        <p className="text-xs text-slate-400">{row.email ?? ""} · #{row.siteId}</p>
                        {row.staxbillsAccountId && (
                          <p className="text-xs text-slate-300">Stax: {row.staxbillsAccountId}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-slate-700">{row.packageName ?? "—"}</p>
                        <p className="text-xs text-slate-400 capitalize">{row.billingInterval}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-700 tabular-nums">
                          ${(row.billedAmount ?? row.baseMonthlyPrice ?? 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">${(row.baseMonthlyPrice ?? 0).toFixed(0)}/mo base</p>
                      </div>
                      <div>
                        {row.parallelInvoiceCount > 0 ? (
                          <>
                            <p className="text-slate-700">{row.parallelInvoiceCount} generated</p>
                            {row.amountMatch !== null && (
                              <p className={`text-xs font-medium ${row.amountMatch ? "text-emerald-600" : "text-red-600"}`}>
                                {row.amountMatch ? "Amounts match" : `Diff: $${Math.abs(row.amountDiff ?? 0).toFixed(2)}`}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">None yet</p>
                        )}
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        {pipelineBadge(row.pipelineState)}
                        {row.importedAt && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {new Date(row.importedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </p>
                        )}
                      </div>
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <i className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"} text-slate-300 text-xs`} />
                      </div>
                    </div>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <div className="bg-slate-50 border-t border-slate-100 px-4 py-4 space-y-4">
                        {/* Timeline */}
                        <div className="flex items-center gap-6 text-xs">
                          {[
                            { label: "Imported", at: row.importedAt, by: row.importedBy, state: "imported" },
                            { label: "Invoice Generated", at: row.lastParallelInvoiceDate, by: null, state: "parallel_invoiced" },
                            { label: "Verified", at: row.verifiedAt, by: row.verifiedBy, state: "verified" },
                            { label: "Live", at: row.cutoverAt, by: row.cutoverBy, state: "live" },
                          ].map((step, i) => {
                            const done = step.at != null;
                            const active = row.pipelineState === step.state;
                            return (
                              <React.Fragment key={step.label}>
                                {i > 0 && <div className={`flex-1 h-px ${done ? "bg-primary-hover" : "bg-slate-200"}`} />}
                                <div className={`flex flex-col items-center gap-0.5 shrink-0 ${active ? "text-primary" : done ? "text-emerald-600" : "text-slate-300"}`}>
                                  <i className={`fa-solid ${done ? "fa-check-circle" : "fa-circle"} text-base`} />
                                  <span className="font-medium">{step.label}</span>
                                  {step.at && <span className="text-slate-400">{new Date(step.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                                  {step.by && <span className="text-slate-300">{step.by.split("@")[0]}</span>}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>

                        {/* Simulation result */}
                        {sim && (
                          <div className={`rounded-lg border p-3 text-xs ${
                            sim.projectedAmount === (row.billedAmount ?? row.baseMonthlyPrice)
                              ? "bg-emerald-50 border-emerald-200"
                              : "bg-amber-50 border-amber-200"
                          }`}>
                            <p className="font-medium text-slate-700 mb-1">Simulation Result</p>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <p className="text-slate-500">Stax charged</p>
                                <p className="font-bold text-slate-800">${(row.billedAmount ?? 0).toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Velaro would charge</p>
                                <p className="font-bold text-slate-800">${sim.projectedAmount.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Difference</p>
                                <p className={`font-bold ${Math.abs(sim.projectedAmount - (row.billedAmount ?? 0)) < 1 ? "text-emerald-600" : "text-red-600"}`}>
                                  {Math.abs(sim.projectedAmount - (row.billedAmount ?? 0)) < 0.01
                                    ? "Match ✓"
                                    : `$${(sim.projectedAmount - (row.billedAmount ?? 0)).toFixed(2)}`}
                                </p>
                              </div>
                            </div>
                            <p className="text-slate-400 mt-2">{sim.lineItemBreakdown}</p>
                            <p className="text-slate-400">Next period: {new Date(sim.nextPeriodStart).toLocaleDateString("en-US")} → {new Date(sim.nextPeriodEnd).toLocaleDateString("en-US")}</p>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleSimulate(row.siteId)}
                            disabled={simulating === row.siteId}
                            className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-white disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {simulating === row.siteId ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-calculator" />}
                            {sim ? "Re-simulate" : "Simulate Invoice"}
                          </button>

                          {(row.pipelineState === "parallel_invoiced" || (sim && row.pipelineState === "imported")) && (
                            <button
                              onClick={() => handleVerify(row)}
                              disabled={verifying === row.siteId}
                              className="px-3 py-1.5 rounded bg-violet-600 text-white text-xs hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {verifying === row.siteId ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-check" />}
                              Mark Verified
                            </button>
                          )}

                          {(row.pipelineState === "verified" || row.pipelineState === "parallel_invoiced") && row.parallelBillingMode && (
                            <button
                              onClick={() => handleCutover(row.siteId)}
                              className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 flex items-center gap-1.5"
                            >
                              <i className="fa-solid fa-check-double" />
                              Cut Over to Live
                            </button>
                          )}

                          <button
                            onClick={() => setPreviewSite(row.siteId)}
                            className="px-3 py-1.5 rounded border border-cornflower-blue-300 text-xs text-primary hover:bg-primary-bg flex items-center gap-1.5"
                          >
                            <i className="fa-solid fa-file-lines" />
                            Preview Invoice
                          </button>

                          <button
                            onClick={() => setBulkDraftSite(row.siteId)}
                            className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"
                            title="Pre-create draft invoices for remaining Stax months"
                          >
                            <i className="fa-solid fa-copy" />
                            Bulk Drafts
                          </button>

                          <button
                            onClick={() => setCancelSite(row.siteId)}
                            className="px-3 py-1.5 rounded border border-red-200 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1.5"
                          >
                            <i className="fa-solid fa-ban" />
                            Cancel
                          </button>

                          {row.nextRenewalDate && (
                            <span className="text-xs text-slate-400 ml-auto">
                              Next renewal: {new Date(row.nextRenewalDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={load} className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
              <i className="fa-solid fa-arrows-rotate" />
              Refresh
            </button>
          </div>
        </>
      )}

      {previewSite !== null && (
        <ProjectedInvoiceModal siteId={previewSite} onClose={() => setPreviewSite(null)} />
      )}

      {cancelSite !== null && (
        <CancelSubscriptionModal
          siteId={cancelSite}
          onClose={() => setCancelSite(null)}
          onDone={() => { setCancelSite(null); load(); }}
        />
      )}

      {bulkDraftSite !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-slate-800">Pre-create Draft Invoices — Site {bulkDraftSite}</p>
              <button onClick={() => setBulkDraftSite(null)} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Creates N monthly draft invoices starting from the chosen date. All are created in
              <strong> draft</strong> status — they won't be sent or synced to QB until you
              activate each one individually.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 block mb-1">First Invoice Date</label>
                <input type="date" value={bulkDraftDate}
                  onChange={e => setBulkDraftDate(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Number of Invoices</label>
                <input type="number" min={1} max={24} value={bulkDraftCount}
                  onChange={e => setBulkDraftCount(Number(e.target.value))}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setBulkDraftSite(null)}
                className="px-4 py-2 rounded border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                disabled={bulkDraftSubmitting}
                onClick={async () => {
                  setBulkDraftSubmitting(true);
                  try {
                    const resp = await api.messaging.post(
                      `superadmin/billing/site/${bulkDraftSite}/bulk-draft-invoices`,
                      { firstInvoiceDate: bulkDraftDate, count: bulkDraftCount }
                    );
                    if (resp.ok) {
                      const data = await resp.json();
                      toastAlert.displayToast(Severity.Success,
                        `Created ${data.invoices?.length ?? 0} draft invoice(s).`);
                      setBulkDraftSite(null);
                    } else {
                      toastAlert.displayToast(Severity.Error, "Failed to create drafts.");
                    }
                  } catch {
                    toastAlert.displayToast(Severity.Error, "Network error.");
                  } finally {
                    setBulkDraftSubmitting(false);
                  }
                }}
                className="px-4 py-2 rounded bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
              >
                {bulkDraftSubmitting ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-copy" />}
                Create {bulkDraftCount} Draft{bulkDraftCount > 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Cancel Subscription Modal ─────────────────────────────────────────────────

function CancelSubscriptionModal({
  siteId,
  onClose,
  onDone,
}: {
  siteId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [earlyTermination, setEarlyTermination] = useState(false);
  const [reason, setReason] = useState("");
  const [attritionType, setAttritionType] = useState("voluntary");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/cancel`, {
        earlyTermination,
        reason: reason || null,
        attritionType,
      });
      const data = await resp.json();
      if (data.success !== false) {
        toastAlert.displayToast(Severity.Success,
          earlyTermination
            ? "Subscription immediately terminated."
            : `Cancellation scheduled for ${data.cancellationEffectiveAt ? new Date(data.cancellationEffectiveAt).toLocaleDateString() : "period end"}.`
        );
        onDone();
      } else {
        toastAlert.displayToast(Severity.Error, data.error ?? "Failed to cancel subscription.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">Cancel Subscription</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Termination Type</p>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={!earlyTermination}
                  onChange={() => setEarlyTermination(false)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">Cancel at Period End</p>
                  <p className="text-xs text-slate-400">Service runs until the current billing period ends, then stops. No invoice generated at next renewal.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={earlyTermination}
                  onChange={() => setEarlyTermination(true)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">Immediate Termination</p>
                  <p className="text-xs text-orange-600">Subscription cancelled now. Any outstanding invoices remain — use Write-Off in Collections to close them.</p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Attrition Type</label>
            <select
              value={attritionType}
              onChange={(e) => setAttritionType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="voluntary">Voluntary</option>
              <option value="non_payment">Non-payment</option>
              <option value="early_termination">Early termination</option>
              <option value="migration">Migration to another system</option>
              <option value="test">Test account</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. customer requested via email, switching to competitor..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>

          {earlyTermination && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-700">
              <i className="fa-solid fa-triangle-exclamation mr-1" />
              This action is immediate. The site will be marked as expired. This cannot be undone without superadmin access.
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm text-white disabled:opacity-50 flex items-center gap-2 ${earlyTermination ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600"}`}
          >
            {saving && <i className="fa-solid fa-spinner fa-spin" />}
            {earlyTermination ? "Terminate Now" : "Schedule Cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── StaxBillTab wrapper ────────────────────────────────────────────────────────

function StaxBillTab() {
  const [subTab, setSubTab] = useState<"bulk" | "single" | "cutover" | "tracker">("bulk");
  const api = useApi();
  const toastAlert = useToastAlert();

  const [email, setEmail] = useState("");
  const [staxId, setStaxId] = useState("");
  const [contractStart, setContractStart] = useState("");
  const [contractEnd, setContractEnd] = useState("");
  const [interval, setInterval] = useState("annual");
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [packageId, setPackageId] = useState("2");
  const [siteId, setSiteId] = useState<number | null>(null);
  const [findingsite, setFindingSite] = useState(false);
  const [importing, setImporting] = useState(false);

  async function handleFindSite() {
    if (!email.trim()) return;
    setFindingSite(true);
    setSiteId(null);
    try {
      const resp = await api.admin.get(
        `api/Site/find-by-email?email=${encodeURIComponent(email.trim())}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setSiteId(data.siteId ?? null);
        if (!data.siteId) toastAlert.displayToast(Severity.Warning, "No site found for that email.");
      } else {
        toastAlert.displayToast(Severity.Error, "Lookup failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setFindingSite(false);
    }
  }

  async function handleImport() {
    if (!siteId) {
      toastAlert.displayToast(Severity.Error, "Find a site first.");
      return;
    }
    if (!contractStart || !monthlyPrice) {
      toastAlert.displayToast(Severity.Error, "Contract Start and Monthly Price are required.");
      return;
    }
    setImporting(true);
    try {
      const anniversaryEl = document.getElementById("_stax_anniversaryDate") as HTMLInputElement | null;
      const upliftEl = document.getElementById("_stax_upliftPercent") as HTMLInputElement | null;
      const upliftIntervalEl = document.getElementById("_stax_upliftInterval") as HTMLSelectElement | null;
      const billingOnlyEl = document.getElementById("_stax_billingOnlyMode") as HTMLInputElement | null;
      const resp = await api.messaging.post(`superadmin/billing/site/${siteId}/migrate`, {
        ExternalId: staxId || `staxbill-import-${siteId}`,
        ExternalSystem: "staxbill",
        ContractStartDate: contractStart,
        ContractEndDate: contractEnd || null,
        BillingInterval: interval,
        BaseMonthlyPrice: parseFloat(monthlyPrice),
        PaymentMethod: paymentMethod,
        PackageId: parseInt(packageId, 10),
        ContractAnniversaryDate: anniversaryEl?.value || null,
        AnnualUpliftPercent: upliftEl?.value ? parseFloat(upliftEl.value) : null,
        UpliftInterval: upliftIntervalEl?.value || "annual",
        BillingOnlyMode: billingOnlyEl?.checked ?? false,
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(
            Severity.Success,
            `Site ${siteId} migrated from Staxbill (subscription ${data.subscriptionId})`
          );
          // Reset form
          setEmail("");
          setStaxId("");
          setContractStart("");
          setContractEnd("");
          setMonthlyPrice("");
          setSiteId(null);
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Import failed.");
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Import failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setImporting(false);
    }
  }

  // ── Single account form render ─────────────────────────────────────────────

  function renderSingleForm() {
    return (
      <div className="space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <p className="text-sm font-medium text-slate-700">Migrate a Single Stax Account to Velaro Billing</p>

          {/* Find site */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-500 mb-1">Customer Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={handleFindSite}
              disabled={findingsite || !email.trim()}
              className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
            >
              {findingsite && <i className="fa-solid fa-spinner fa-spin" />}
              Find Site
            </button>
          </div>

          {siteId && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm text-emerald-700">
              Found: <strong>Site {siteId}</strong>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Staxbill Account ID</label>
            <input
              type="text"
              value={staxId}
              onChange={(e) => setStaxId(e.target.value)}
              placeholder="Optional — used as ExternalId reference"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Contract Start *</label>
              <input
                type="date"
                value={contractStart}
                onChange={(e) => setContractStart(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Contract End</label>
              <input
                type="date"
                value={contractEnd}
                onChange={(e) => setContractEnd(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Interval</label>
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {BILLING_INTERVALS.map((bi) => (
                  <option key={bi} value={bi}>
                    {bi.charAt(0).toUpperCase() + bi.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Price *</label>
              <input
                type="number"
                value={monthlyPrice}
                onChange={(e) => setMonthlyPrice(e.target.value)}
                placeholder="249.00"
                step="0.01"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Package</label>
            <select
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="1">Starter</option>
              <option value="2">Professional</option>
              <option value="3">Enterprise</option>
            </select>
          </div>

          {/* Contract term settings */}
          <details className="group">
            <summary className="cursor-pointer text-xs text-primary hover:underline select-none flex items-center gap-1">
              <i className="fa-solid fa-chevron-right group-open:rotate-90 transition-transform text-[10px]" />
              Advanced: Contract Anniversary &amp; Uplift
            </summary>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Contract Anniversary Date
                  <span className="ml-1 text-slate-400 font-normal">(annual price review)</span>
                </label>
                <input
                  type="date"
                  value={(StaxBillTab as any)._anniversaryDate ?? ""}
                  onChange={(e) => {
                    // Store in DOM for form use — simplest approach for rarely-used field
                    (document.getElementById("_stax_anniversaryDate") as HTMLInputElement)!.value = e.target.value;
                  }}
                  id="_stax_anniversaryDate"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  Leave blank to default to Contract Start. Set this when the customer is billed monthly but has an annual pricing review date.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Uplift %
                  <span className="ml-1 text-slate-400 font-normal">(applied at each interval)</span>
                </label>
                <input
                  type="number"
                  id="_stax_upliftPercent"
                  step="0.1"
                  placeholder="e.g. 5"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Uplift Fires
                  <span className="ml-1 text-slate-400 font-normal">(independent of billing interval)</span>
                </label>
                <select
                  id="_stax_upliftInterval"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {UPLIFT_INTERVALS.map((ui) => (
                    <option key={ui.value} value={ui.value}>{ui.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="_stax_billingOnlyMode"
                    className="rounded border-slate-300 text-primary"
                  />
                  <span className="text-xs text-slate-700">
                    Billing-only mode
                    <span className="ml-1 text-slate-400 font-normal">
                      — collect payment but don't gate features (use for V10 legacy accounts still running in old system)
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </details>

          <div className="flex justify-end">
            <button
              onClick={handleImport}
              disabled={importing || !siteId}
              className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
            >
              {importing && <i className="fa-solid fa-spinner fa-spin" />}
              Import to Velaro Billing
            </button>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500">
          Creates a <code>BillingSubscription</code> with <code>ExternalBillingSystem = "staxbill"</code> in parallel mode.
          Switch to <strong>Cutover Queue</strong> when ready to go live.
        </div>
      </div>
    );
  }

  // ── Sub-tab nav + render ───────────────────────────────────────────────────

  const SUB_TABS = [
    { id: "bulk" as const, label: "Bulk CSV Import", icon: "fa-file-csv" },
    { id: "single" as const, label: "Single Account", icon: "fa-user" },
    { id: "cutover" as const, label: "Cutover Queue", icon: "fa-check-double" },
    { id: "tracker" as const, label: "Migration Tracker", icon: "fa-timeline" },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tab nav */}
      <div className="flex gap-1 border-b border-slate-200">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 -mb-px transition ${
              subTab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <i className={`fa-solid ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "bulk" && <BulkCsvImport />}
      {subTab === "single" && renderSingleForm()}
      {subTab === "cutover" && (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-slate-700">Cutover Queue</p>
            <p className="text-xs text-slate-400 mt-0.5">
              These accounts are in parallel billing mode — invoices are generating but email/QB sync is suppressed.
              Select accounts and click <strong>Cut Over</strong> to go live on Velaro billing.
            </p>
          </div>
          <ParallelSitesList />
        </div>
      )}
      {subTab === "tracker" && <MigrationTracker />}
    </div>
  );
}

// ── Package Management Tab ────────────────────────────────────────────────────

interface PkgFeatures {
  enableWeb?: boolean; enableSms?: boolean; enableEmail?: boolean;
  enableWhatsapp?: boolean; enableIvr?: boolean; enableFacebook?: boolean;
  enableAI?: boolean; enableAdvancedAIFeatures?: boolean; enableSemanticSearch?: boolean;
  enableMcp?: boolean; enableOrchestration?: boolean; enableCustomAiModel?: boolean;
  enableWorkflowRules?: boolean; enableAutomation?: boolean;
  enableShopify?: boolean; enableNetsuite?: boolean; enableHubSpot?: boolean;
  enableHubSpotSkills?: boolean; enableBigCommerce?: boolean; enableDynamics365?: boolean;
  enableServiceNow?: boolean; enableSalesforce?: boolean; enablePipedrive?: boolean;
  enableNetSuiteSkills?: boolean; enableZapier?: boolean; enablePowerAutomate?: boolean; enableMake?: boolean;
  enableEnterpriseSso?: boolean; enablePasskeys?: boolean; enableAdvancedMfa?: boolean;
  enableIpBlocking?: boolean; enableMessageScrubbing?: boolean; enableCoBrowse?: boolean;
  enableAuditReporting?: boolean; enableConversationReports?: boolean; enableSentimentReports?: boolean;
  enableAgentPerformanceReports?: boolean; enableResponseTimeReports?: boolean;
  enableCallReports?: boolean; enableIvrReports?: boolean; enableOpsIntelligence?: boolean;
  enableTicketing?: boolean; enableKnowledgeBase?: boolean; enableContactManager?: boolean;
  enableClickToCall?: boolean; enableRecruitingBot?: boolean; enableEcommerceSync?: boolean;
  enableIntelligenceSuite?: boolean; enableCustomDashboards?: boolean;
  maxLicensedUsers?: number; maxCreatedUsers?: number; maxConversationsPerMonth?: number;
  maxIvrMinutesPerMonth?: number; maxSmsPerMonth?: number; maxLicensedUsers0?: number;
  inboxRetentionDays?: number; maxExportDays?: number; maxKbArticles?: number;
  maxActiveWorkflows?: number; maxWorkflowRunsPerMonth?: number; maxAiToolCallsPerMonth?: number;
  maxAvatarVoiceCharsPerMonth?: number; maxSsoProviders?: number;
  [key: string]: boolean | number | string | undefined | null;
}

interface PkgVersion {
  id: number;
  versionNumber: number;
  patchNumber?: number;
  changeNotes?: string;
  isPublished: boolean;
  createdAt: string;
  createdBy: string;
  features?: PkgFeatures;
}

interface PkgDetail {
  id: number;
  name: string;
  description?: string;
  color?: string;
  isSystemPackage: boolean;
  sortOrder: number;
  createdAt: string;
  createdBy: string;
  versions: PkgVersion[];
}

interface PkgListItem {
  id: number;
  name: string;
  description?: string;
  color?: string;
  isSystemPackage: boolean;
  isCustom: boolean;
  sortOrder: number;
  latestVersion: number;
  versionCount: number;
  activeSiteCount: number;
  prices?: { id: number; interval: string; baseMonthlyPrice: number; billedAmount: number }[];
}

interface FieldDiffRow {
  fieldName: string;
  packageValue: string;
  currentValue: string;
  isOverridden: boolean;
}

interface VersionReportRow {
  siteId: number;
  companyName: string | null;
  packageId: number;
  packageName: string;
  currentVersionId: number | null;
  currentVersionLabel: string;
  latestVersionId: number | null;
  latestVersionLabel: string;
  isLatest: boolean;
  autoUpgrade: boolean;
  overrideCount: number;
}

const FLAG_GROUPS: { label: string; fields: { key: string; label: string }[] }[] = [
  { label: "Channels", fields: [
    { key: "enableWeb", label: "Web Chat" },
    { key: "enableSms", label: "SMS" },
    { key: "enableEmail", label: "Email" },
    { key: "enableWhatsapp", label: "WhatsApp" },
    { key: "enableIvr", label: "IVR / Voice" },
    { key: "enableFacebook", label: "Facebook" },
  ]},
  { label: "AI & Bots", fields: [
    { key: "enableAI", label: "AI Bots" },
    { key: "enableAdvancedAIFeatures", label: "Advanced AI" },
    { key: "enableSemanticSearch", label: "Semantic Search" },
    { key: "enableMcp", label: "MCP" },
    { key: "enableOrchestration", label: "AI Orchestration" },
    { key: "enableCustomAiModel", label: "Custom AI Model" },
    { key: "enableWorkflowRules", label: "Workflow Rules" },
    { key: "enableAutomation", label: "Automation" },
    { key: "enableRecruitingBot", label: "Recruiting Bot" },
  ]},
  { label: "Integrations", fields: [
    { key: "enableShopify", label: "Shopify" },
    { key: "enableNetsuite", label: "NetSuite" },
    { key: "enableHubSpot", label: "HubSpot" },
    { key: "enableHubSpotSkills", label: "HubSpot AI Skills" },
    { key: "enableBigCommerce", label: "BigCommerce" },
    { key: "enableDynamics365", label: "Dynamics 365" },
    { key: "enableServiceNow", label: "ServiceNow" },
    { key: "enableSalesforce", label: "Salesforce" },
    { key: "enablePipedrive", label: "Pipedrive" },
    { key: "enableNetSuiteSkills", label: "NetSuite AI Skills" },
    { key: "enableZapier", label: "Zapier" },
    { key: "enablePowerAutomate", label: "Power Automate" },
    { key: "enableMake", label: "Make" },
  ]},
  { label: "Security", fields: [
    { key: "enableEnterpriseSso", label: "Enterprise SSO" },
    { key: "enablePasskeys", label: "Passkeys" },
    { key: "enableAdvancedMfa", label: "Advanced MFA" },
    { key: "enableIpBlocking", label: "IP Blocking" },
    { key: "enableMessageScrubbing", label: "Message Scrubbing (PII)" },
  ]},
  { label: "Features", fields: [
    { key: "enableTicketing", label: "Ticketing" },
    { key: "enableKnowledgeBase", label: "Knowledge Base" },
    { key: "enableContactManager", label: "Contact Manager" },
    { key: "enableClickToCall", label: "Click-to-Call" },
    { key: "enableCoBrowse", label: "Co-browse" },
    { key: "enableEcommerceSync", label: "Ecommerce Sync" },
    { key: "enableCustomDashboards", label: "Custom Dashboards" },
    { key: "enableIntelligenceSuite", label: "Intelligence Suite" },
  ]},
  { label: "Reports", fields: [
    { key: "enableAuditReporting", label: "Audit Reports" },
    { key: "enableConversationReports", label: "Conversation Reports" },
    { key: "enableSentimentReports", label: "Sentiment Reports" },
    { key: "enableAgentPerformanceReports", label: "Agent Performance" },
    { key: "enableResponseTimeReports", label: "Response Time" },
    { key: "enableCallReports", label: "Call Reports" },
    { key: "enableIvrReports", label: "IVR Reports" },
    { key: "enableOpsIntelligence", label: "Ops Intelligence" },
  ]},
];

const LIMIT_FIELDS: { key: string; label: string }[] = [
  { key: "maxLicensedUsers", label: "Max Licensed Users" },
  { key: "maxCreatedUsers", label: "Max Created Users" },
  { key: "maxConversationsPerMonth", label: "Max Conversations/Month (0=∞)" },
  { key: "maxIvrMinutesPerMonth", label: "Max IVR Min/Month (0=∞)" },
  { key: "maxSmsPerMonth", label: "Max SMS/Month (0=∞)" },
  { key: "inboxRetentionDays", label: "Inbox Retention Days" },
  { key: "maxExportDays", label: "Max Export Days" },
  { key: "maxKbArticles", label: "Max KB Articles" },
  { key: "maxActiveWorkflows", label: "Max Active Workflows" },
  { key: "maxWorkflowRunsPerMonth", label: "Max Workflow Runs/Month (0=∞)" },
  { key: "maxAiToolCallsPerMonth", label: "Max AI Tool Calls/Month (0=∞)" },
  { key: "maxAvatarVoiceCharsPerMonth", label: "Max Avatar Voice Chars/Month (0=∞)" },
  { key: "maxSsoProviders", label: "Max SSO Providers" },
];

const PKG_COLORS: Record<string, string> = {
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function pkgColorClass(color?: string) {
  return PKG_COLORS[color ?? "slate"] ?? PKG_COLORS.slate;
}

function FeatureEditorModal({
  pkg,
  version,
  onClose,
  onSaved,
}: {
  pkg: PkgDetail;
  version: PkgVersion;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [flags, setFlags] = useState<PkgFeatures>({ ...(version.features ?? {}) });
  const [saving, setSaving] = useState(false);

  function setFlag(key: string, val: boolean | number) {
    setFlags((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const resp = await api.messaging.put(`superadmin/billing/packages/versions/${version.id}`, {
        changeNotes: version.changeNotes,
        features: flags,
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(Severity.Success, "Features saved.");
          onSaved();
          onClose();
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Save failed.");
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Save failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <p className="text-base font-semibold text-slate-800">
              Edit Features — {pkg.name} v{version.versionNumber}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">{version.isPublished ? "Published — read-only" : "Draft — editable"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {FLAG_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group.label}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {group.fields.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!flags[key]}
                      disabled={version.isPublished}
                      onChange={(e) => setFlag(key, e.target.checked)}
                      className="rounded border-slate-300 text-primary disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Limits</p>
            <div className="grid grid-cols-2 gap-3">
              {LIMIT_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-slate-500 mb-0.5">{label}</label>
                  <input
                    type="number"
                    value={(flags[key] as number | undefined) ?? 0}
                    disabled={version.isPublished}
                    onChange={(e) => setFlag(key, parseInt(e.target.value, 10) || 0)}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">
            {version.isPublished ? "Close" : "Cancel"}
          </button>
          {!version.isPublished && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <i className="fa-solid fa-spinner fa-spin" />}
              Save Features
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplyPackageModal({
  pkg,
  version,
  onClose,
}: {
  pkg: PkgDetail;
  version: PkgVersion;
  onClose: () => void;
}) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [siteId, setSiteId] = useState("");
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diff, setDiff] = useState<FieldDiffRow[] | null>(null);
  const [resetOverrides, setResetOverrides] = useState(false);
  const [applying, setApplying] = useState(false);

  async function handleLoadDiff() {
    const id = parseInt(siteId, 10);
    if (!id) return;
    setLoadingDiff(true);
    setDiff(null);
    try {
      const resp = await api.messaging.get(`superadmin/billing/site/${id}/package-diff`);
      if (resp.ok) {
        const data = await resp.json();
        setDiff(Array.isArray(data.diffs) ? data.diffs : []);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to load diff.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setLoadingDiff(false);
    }
  }

  async function handleApply() {
    const id = parseInt(siteId, 10);
    if (!id) return;
    if (!confirm(`Apply ${pkg.name} v${version.versionNumber} to site ${id}? This will overwrite ${diff?.length ?? "?"} field(s).`)) return;
    setApplying(true);
    try {
      const resp = await api.messaging.post(`superadmin/billing/site/${id}/apply-package`, {
        packageId: pkg.id,
        versionId: version.id,
        autoUpgrade: false,
        resetOverrides,
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(Severity.Success, `Applied ${pkg.name} v${version.versionNumber} — ${data.fieldsChanged?.length ?? 0} field(s) changed.`);
          onClose();
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Apply failed.");
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Apply failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <p className="text-base font-semibold text-slate-800">Apply {pkg.name} v{version.versionNumber} to Site</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex gap-2">
            <input
              type="number"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="Site ID"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={handleLoadDiff}
              disabled={loadingDiff || !siteId}
              className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
            >
              {loadingDiff ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-arrows-left-right" />}
              Preview Changes
            </button>
          </div>

          {diff !== null && (
            <div>
              {diff.length === 0 ? (
                <p className="text-sm text-emerald-600 font-medium">
                  <i className="fa-solid fa-check-circle mr-1" />
                  No differences — site already matches this package version.
                </p>
              ) : (
                <>
                  <p className="text-xs font-medium text-slate-500 mb-2">{diff.length} field(s) will change:</p>
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                    <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-1.5 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-200">
                      <span>Field</span><span>Current</span><span>Package Value</span>
                    </div>
                    {diff.map((row) => (
                      <div key={row.fieldName} className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-0">
                        <span className="font-mono text-slate-700">{row.fieldName}{row.isOverridden && <span className="ml-1 text-amber-500" title="Overridden">*</span>}</span>
                        <span className="text-slate-500">{row.currentValue || "—"}</span>
                        <span className="text-primary font-medium">{row.packageValue || "—"}</span>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={resetOverrides}
                      onChange={(e) => setResetOverrides(e.target.checked)}
                      className="rounded border-slate-300 text-primary"
                    />
                    <span className="text-xs text-slate-600">Reset per-site overrides (fields marked * will be replaced)</span>
                  </label>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={handleApply}
            disabled={applying || !siteId || diff === null}
            className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-2"
          >
            {applying && <i className="fa-solid fa-spinner fa-spin" />}
            Apply to Site
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PACKAGES GUIDE — keep this in sync whenever you add/change billing features.
//
// RULE: if you ship a new endpoint, new modal, or new workflow in this tab,
// add or update the matching section below. The guide is the living manual for
// whoever operates billing. Stale guides are worse than no guide.
//
// Each section: { title, icon, steps: string[], example?: string }
// ─────────────────────────────────────────────────────────────────────────────
const PACKAGES_GUIDE: {
  title: string;
  icon: string;
  steps: string[];
  example?: string;
}[] = [
  {
    title: "Migrate customers to a new package version",
    icon: "fa-arrow-right-arrow-left",
    steps: [
      'Open the package you want to update (e.g. "Growth").',
      'Click "Capture from Site" and enter any customer site ID that has the config you want. This creates a draft version — nothing is changed yet.',
      'Click "Edit Features" on the draft to review or tweak the flags and limits.',
      'Click "Publish" to lock the version in. Published versions cannot be edited.',
      'Open the Upgrade Report (bottom of this tab). Set filter to "Outdated Only".',
      'Check the sites you want to migrate. Tip: "Override count" shows sites with custom settings — review those individually before upgrading.',
      'Click "Upgrade Selected". Leave "Reset overrides" OFF to preserve any per-site customizations, or turn it ON to enforce the package exactly.',
    ],
    example: 'Example: You added IVR support to Growth. Capture from a site that already has it enabled -> publish as v3 -> Upgrade Report shows 40 sites on v1 or v2 -> select all -> upgrade. Sites with a custom override (e.g. extra SMS limit) keep their override unless you reset.',
  },
  {
    title: "Create a new package (blank vs. sync from site)",
    icon: "fa-box",
    steps: [
      'Click "New Package".',
      'Choose "Blank" if you are building a new tier from scratch — all flags default to off, set them via Edit Features.',
      "Choose \"Sync from Site\" if a customer already has the exact config you want. Enter their site ID — the package is created with v1 already set to that site's live flags and published immediately.",
      'Name it something recognizable (e.g. "Enterprise Plus", "Healthcare Tier").',
      'After creation, expand the package and use "Edit Features" to adjust before applying to anyone.',
    ],
    example: 'Example: A large enterprise customer negotiated a custom config. You want to offer the same to similar accounts. Enter their site ID in "Sync from Site" — a new package appears pre-configured. Rename it, then apply to future accounts.',
  },
  {
    title: "Apply a package to one account",
    icon: "fa-user-check",
    steps: [
      'Expand the package and find the published version you want.',
      'Click "Apply to Site" and enter the site ID.',
      'Review the diff: green = being added, red = being removed, yellow = being changed.',
      'If the account has custom overrides (e.g. they paid for extra agents), leave "Reset overrides" OFF — those fields are skipped.',
      'Turn "Reset overrides" ON only if you want the package to take full effect and wipe all customizations.',
      'Confirm. Changes take effect immediately.',
    ],
    example: 'Example: A new customer signs up for Growth. Apply Growth v3 — diff shows EnableIvr turning on and MaxLicensedUsers going from 5 to 10 — confirm — their account is upgraded immediately.',
  },
  {
    title: "What overrides mean (and when to reset them)",
    icon: "fa-sliders",
    steps: [
      'An "override" is any field on a subscription that differs from the package template — set manually, by a previous one-off deal, or by an earlier bulk upgrade.',
      'Overrides survive upgrades by default. This protects customers who paid for extras (more seats, higher SMS limits).',
      'The Upgrade Report shows the override count per site in amber. Anything above 0 means that site will be partially upgraded — the overridden fields are skipped.',
      'To wipe overrides: check "Reset overrides" before upgrading. Use this only when standardizing a tier and you are sure no custom deals are in play.',
      'To see exactly what is overridden for one account: expand the package, click "Apply to Site", enter the site ID — the diff shows overridden fields in yellow.',
    ],
    example: 'Example: Site 4421 shows 3 overrides. Apply to Site — diff shows MaxLicensedUsers=25 (package=10), EnableWhatsapp=true (package=false), MaxSmsPerMonth=5000 (package=1000). These are likely paid add-ons — leave Reset OFF when upgrading.',
  },
  {
    title: "Convert an invoice to a subscription",
    icon: "fa-file-invoice",
    steps: [
      'Go to Collections tab and find the invoice.',
      'Use "Convert to Subscription" (POST superadmin/billing/invoice/{id}/convert-to-subscription).',
      'The interval is inferred from the invoice period: under 45 days = monthly, under 120 = quarterly, under 450 = annual, etc.',
      'The new subscription starts as "paused" with AutoRenew=false — safe to review before activating.',
      "Optionally attach a PackageId so the account's feature flags are set immediately.",
    ],
    example: 'Example: A customer paid a $2,400 annual invoice but has no subscription record. Convert it — system creates a paused annual sub at $200/mo — attach Growth package — activate.',
  },
  {
    title: "Auto-upgrade vs. pinned versions",
    icon: "fa-lock",
    steps: [
      'Every subscription has a PackageVersionId pin — sites stay on that version until manually upgraded.',
      'Sites with AutoUpgradePackageVersion=true move to new published versions automatically when you publish.',
      'The Upgrade Report "Auto-Up" column shows which sites have this enabled.',
      'New customers should have AutoUpgrade ON unless they negotiated a locked version.',
      'Use the Upgrade Report to manually push older customers who have it OFF.',
    ],
    example: 'Example: You publish Growth v4. Sites with Auto-Up ON move immediately. The Upgrade Report shows 12 sites still on v3 with Auto-Up OFF — these are older accounts or custom deals. Review and upgrade manually.',
  },
];

function PackagesGuidePanel({ onClose }: { onClose: () => void }) {
  const [active, setActive] = React.useState(0);
  const guide = PACKAGES_GUIDE[active];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-sm font-semibold text-slate-800">Billing Package Guide</p>
            <p className="text-xs text-slate-400 mt-0.5">Step-by-step workflows for operating the package system.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Nav */}
        <div className="px-5 pt-4 space-y-1">
          {PACKAGES_GUIDE.map((g, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition ${
                active === i ? "bg-primary-bg text-primary-active font-medium" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <i className={`fa-solid ${g.icon} text-xs w-4 text-center ${active === i ? "text-primary" : "text-slate-400"}`} />
              {g.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-5 py-5 border-t border-slate-100 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-primary-bg flex items-center justify-center shrink-0">
              <i className={`fa-solid ${guide.icon} text-xs text-primary`} />
            </div>
            <p className="text-sm font-semibold text-slate-800">{guide.title}</p>
          </div>

          <ol className="space-y-3">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <p className="text-sm text-slate-700 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>

          {guide.example && (
            <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">Real example</p>
              <p className="text-xs text-amber-800 leading-relaxed">{guide.example}</p>
            </div>
          )}
        </div>

        <div className="px-5 pb-6 pt-2 border-t border-slate-100 mt-2">
          <p className="text-[10px] text-slate-400 italic">
            This guide lives in <code className="bg-slate-100 px-1 rounded">BillingFinancial/index.tsx</code> → <code className="bg-slate-100 px-1 rounded">PACKAGES_GUIDE</code>. Update it whenever a feature changes.
          </p>
        </div>
      </div>
    </div>
  );
}

function PackagesTab() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [packages, setPackages] = useState<PkgListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PkgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editModal, setEditModal] = useState<{ pkg: PkgDetail; version: PkgVersion } | null>(null);
  const [applyModal, setApplyModal] = useState<{ pkg: PkgDetail; version: PkgVersion } | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newColor, setNewColor] = useState("blue");
  const [fromSiteId, setFromSiteId] = useState("");       // for "create from site" option
  const [useFromSite, setUseFromSite] = useState(false);  // toggle in create form
  const [creating, setCreating] = useState(false);
  const [newIsCustom, setNewIsCustom] = useState(false);
  const [pkgTypeFilter, setPkgTypeFilter] = useState<"all" | "standard" | "custom">("all");
  const [showGuide, setShowGuide] = useState(false);

  // Capture-from-site state (per package)
  const [captureForPkgId, setCaptureForPkgId] = useState<number | null>(null);
  const [captureSiteId, setCaptureSiteId] = useState("");
  const [captureNotes, setCaptureNotes] = useState("");
  const [capturing, setCapturing] = useState(false);

  // Upgrade report state
  const [showReport, setShowReport] = useState(false);
  const [reportRows, setReportRows] = useState<VersionReportRow[]>([]);
  const [reportPackages, setReportPackages] = useState<{ id: number; name: string }[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilter, setReportFilter] = useState<"all" | "outdated">("outdated");
  const [reportPkgFilter, setReportPkgFilter] = useState<number | "all">("all");
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(new Set());
  const [upgrading, setUpgrading] = useState(false);
  const [resetOverridesOnUpgrade, setResetOverridesOnUpgrade] = useState(false);

  async function loadPackages() {
    setLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/packages");
      if (resp.ok) {
        const data = await resp.json();
        setPackages(Array.isArray(data) ? data : []);
      }
    } catch { setPackages([]); } finally { setLoading(false); }
  }

  // Load packages once on mount
  useEffect(() => { loadPackages(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleExpand(id: number) {
    if (expandedId === id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await api.messaging.get(`superadmin/billing/packages/${id}`);
      if (resp.ok) setDetail(await resp.json());
    } catch { setDetail(null); } finally { setDetailLoading(false); }
  }

  async function handlePublish(versionId: number) {
    if (!confirm("Publish this version? Published versions cannot be edited.")) return;
    try {
      const resp = await api.messaging.post(`superadmin/billing/packages/versions/${versionId}/publish?autoUpgrade=false`, {});
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(Severity.Success, `Published v${data.versionNumber}.`);
          if (expandedId) {
            setDetail(null);
            setDetailLoading(true);
            const r2 = await api.messaging.get(`superadmin/billing/packages/${expandedId}`);
            if (r2.ok) setDetail(await r2.json());
            setDetailLoading(false);
          }
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Publish failed.");
        }
      }
    } catch { toastAlert.displayToast(Severity.Error, "Network error."); }
  }

  async function handleCreatePackage() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      if (useFromSite && fromSiteId) {
        // Create package + v1 from a site's live subscription config
        const siteIdNum = parseInt(fromSiteId, 10);
        const resp = await api.messaging.post(`superadmin/billing/packages/create-from-site/${siteIdNum}`, {
          name: newName.trim(),
          description: newDesc.trim() || null,
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success) {
            toastAlert.displayToast(Severity.Success, `Package "${newName}" created from site ${siteIdNum}.`);
            setShowCreateForm(false);
            setNewName(""); setNewDesc(""); setNewColor("blue"); setFromSiteId(""); setUseFromSite(false);
            loadPackages();
          } else {
            toastAlert.displayToast(Severity.Error, data.error ?? "Failed to create.");
          }
        }
      } else {
        const resp = await api.messaging.post("superadmin/billing/packages", {
          name: newName.trim(),
          description: newDesc.trim() || null,
          color: newColor,
          sortOrder: 100,
          isActive: true,
          isCustom: newIsCustom,
        });
        if (resp.ok) {
          toastAlert.displayToast(Severity.Success, `Package "${newName}" created${newIsCustom ? " (custom/private)" : ""}.`);
          setShowCreateForm(false);
          setNewName(""); setNewDesc(""); setNewColor("blue"); setNewIsCustom(false);
          loadPackages();
        }
      }
    } catch { toastAlert.displayToast(Severity.Error, "Network error."); } finally { setCreating(false); }
  }

  async function handleCaptureFromSite(pkgId: number) {
    const siteIdNum = parseInt(captureSiteId, 10);
    if (!siteIdNum) return;
    setCapturing(true);
    try {
      const resp = await api.messaging.post(
        `superadmin/billing/packages/${pkgId}/version-from-site?siteId=${siteIdNum}`,
        { changeNotes: captureNotes.trim() || `Captured from site ${siteIdNum}` }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          toastAlert.displayToast(Severity.Success, `Created draft v${data.versionNumber} from site ${siteIdNum}. Review and publish when ready.`);
          setCaptureForPkgId(null);
          setCaptureSiteId("");
          setCaptureNotes("");
          // Refresh detail panel
          setDetail(null);
          setDetailLoading(true);
          const r2 = await api.messaging.get(`superadmin/billing/packages/${pkgId}`);
          if (r2.ok) setDetail(await r2.json());
          setDetailLoading(false);
        } else {
          toastAlert.displayToast(Severity.Error, data.error ?? "Capture failed.");
        }
      }
    } catch { toastAlert.displayToast(Severity.Error, "Network error."); } finally { setCapturing(false); }
  }

  async function loadReport() {
    setReportLoading(true);
    setSelectedSiteIds(new Set());
    try {
      const params = new URLSearchParams();
      if (reportFilter === "outdated") params.set("onlyOutdated", "true");
      if (reportPkgFilter !== "all") params.set("packageId", String(reportPkgFilter));
      const resp = await api.messaging.get(`superadmin/billing/package-version-report?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setReportRows(Array.isArray(data.rows) ? data.rows : []);
        setReportPackages(Array.isArray(data.packages) ? data.packages : []);
      }
    } catch { setReportRows([]); } finally { setReportLoading(false); }
  }

  async function handleUpgradeSelected() {
    if (selectedSiteIds.size === 0) return;

    // Group selected sites by latest version — upgrade each group to its package's latest
    const byVersion = new Map<number, number[]>();
    for (const row of reportRows) {
      if (!selectedSiteIds.has(row.siteId) || !row.latestVersionId) continue;
      const existing = byVersion.get(row.latestVersionId) ?? [];
      existing.push(row.siteId);
      byVersion.set(row.latestVersionId, existing);
    }

    if (byVersion.size === 0) return;

    setUpgrading(true);
    let totalUpgraded = 0;
    let totalSkipped = 0;
    let anyError = false;

    try {
      for (const [versionId, siteIds] of byVersion) {
        const resp = await api.messaging.post("superadmin/billing/upgrade-sites", {
          siteIds,
          versionId,
          resetOverrides: resetOverridesOnUpgrade,
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success) {
            totalUpgraded += data.upgraded ?? 0;
            totalSkipped += data.skipped ?? 0;
          } else { anyError = true; }
        } else { anyError = true; }
      }

      if (anyError) {
        toastAlert.displayToast(Severity.Warning, `Upgrade partially completed. ${totalUpgraded} upgraded, ${totalSkipped} skipped.`);
      } else {
        toastAlert.displayToast(Severity.Success, `Upgraded ${totalUpgraded} site${totalUpgraded !== 1 ? "s" : ""} to latest version.${totalSkipped > 0 ? ` ${totalSkipped} already current.` : ""}`);
      }
      loadReport();
      loadPackages();
    } catch { toastAlert.displayToast(Severity.Error, "Network error during upgrade."); } finally { setUpgrading(false); }
  }

  if (loading) return <div className="py-10 text-center text-slate-400 text-sm"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading packages…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">Subscription Packages</p>
          <p className="text-xs text-slate-400 mt-0.5">Manage feature flags and limits per plan tier. Apply packages to specific accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuide(true)}
            className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-500 text-xs font-medium hover:bg-slate-50 flex items-center gap-1.5"
            title="How to use packages — step-by-step guides"
          >
            <i className="fa-solid fa-circle-question" />
            Guide
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary-active flex items-center gap-1.5"
          >
            <i className="fa-solid fa-plus" />
            New Package
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-700">Create Package</p>

          {/* Source toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setUseFromSite(false)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${!useFromSite ? "bg-primary text-white border-primary" : "border-slate-300 text-slate-500 hover:bg-white"}`}
            >
              Blank
            </button>
            <button
              onClick={() => setUseFromSite(true)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${useFromSite ? "bg-primary text-white border-primary" : "border-slate-300 text-slate-500 hover:bg-white"}`}
            >
              <i className="fa-solid fa-arrows-rotate mr-1" />
              Sync from Site
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-0.5">Name *</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Enterprise Plus" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            {!useFromSite ? (
              <div>
                <label className="block text-xs text-slate-500 mb-0.5">Color</label>
                <select value={newColor} onChange={(e) => setNewColor(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary">
                  {Object.keys(PKG_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-slate-500 mb-0.5">Source Site ID *</label>
                <input type="number" value={fromSiteId} onChange={(e) => setFromSiteId(e.target.value)}
                  placeholder="e.g. 1234" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs text-slate-500 mb-0.5">Description</label>
              <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Optional description" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          {useFromSite && (
            <p className="text-xs text-slate-400">
              Creates a new package with v1 populated from that site's current feature flags — published immediately. Use this to turn a custom-configured account into a reusable template.
            </p>
          )}
          {!useFromSite && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={newIsCustom} onChange={e => setNewIsCustom(e.target.checked)} className="rounded" />
              <span>
                <span className="font-medium">Custom / Account-specific</span>
                <span className="text-slate-400 ml-1">— hidden from self-serve checkout catalog</span>
              </span>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowCreateForm(false); setUseFromSite(false); setFromSiteId(""); setNewIsCustom(false); }} className="px-3 py-1.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:bg-slate-100">Cancel</button>
            <button onClick={handleCreatePackage} disabled={creating || !newName.trim() || (useFromSite && !fromSiteId)}
              className="px-4 py-1.5 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-1.5">
              {creating && <i className="fa-solid fa-spinner fa-spin" />}
              Create
            </button>
          </div>
        </div>
      )}

      {/* Package type filter */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
          {(["all", "standard", "custom"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setPkgTypeFilter(f)}
              className={`px-3 py-1.5 font-medium transition capitalize ${pkgTypeFilter === f ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-50"}`}
            >
              {f === "all" ? `All (${packages.length})` : f === "standard" ? `Standard (${packages.filter(p => !p.isCustom).length})` : `Custom (${packages.filter(p => p.isCustom).length})`}
            </button>
          ))}
        </div>
        {pkgTypeFilter === "custom" && (
          <p className="text-xs text-slate-400">Custom packages are account-specific and hidden from the self-serve checkout catalog.</p>
        )}
      </div>

      <div className="space-y-2">
        {packages.filter(pkg => pkgTypeFilter === "all" || (pkgTypeFilter === "custom" ? pkg.isCustom : !pkg.isCustom)).map((pkg) => (
          <div key={pkg.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <button
              onClick={() => handleExpand(pkg.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
            >
              <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${pkgColorClass(pkg.color)}`}>{pkg.color ?? "blue"}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{pkg.name}</p>
                {pkg.description && <p className="text-xs text-slate-400 truncate">{pkg.description}</p>}
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 shrink-0">
                <span>v{pkg.latestVersion}</span>
                <span>{pkg.activeSiteCount} site{pkg.activeSiteCount !== 1 ? "s" : ""}</span>
                {pkg.isSystemPackage && <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">system</span>}
                {pkg.isCustom && <span className="bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded text-[10px]">custom</span>}
              </div>
              <i className={`fa-solid fa-chevron-right text-slate-400 text-xs transition-transform ${expandedId === pkg.id ? "rotate-90" : ""}`} />
            </button>

            {expandedId === pkg.id && (
              <div className="border-t border-slate-100 px-4 py-4">
                {detailLoading && <p className="text-xs text-slate-400 py-2"><i className="fa-solid fa-spinner fa-spin mr-1" />Loading versions…</p>}
                {detail && detail.id === pkg.id && (
                  <div className="space-y-2">
                    {/* Capture from site — create a new draft version from a live account */}
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-slate-500">Versions</p>
                      {captureForPkgId === pkg.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={captureSiteId}
                            onChange={(e) => setCaptureSiteId(e.target.value)}
                            placeholder="Site ID"
                            className="w-28 rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          <input
                            type="text"
                            value={captureNotes}
                            onChange={(e) => setCaptureNotes(e.target.value)}
                            placeholder="Change notes (optional)"
                            className="w-44 rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          <button
                            onClick={() => handleCaptureFromSite(pkg.id)}
                            disabled={capturing || !captureSiteId}
                            className="px-2.5 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {capturing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-arrows-rotate" />}
                            Pull
                          </button>
                          <button onClick={() => { setCaptureForPkgId(null); setCaptureSiteId(""); setCaptureNotes(""); }}
                            className="text-slate-400 hover:text-slate-600 text-xs">Cancel</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setCaptureForPkgId(pkg.id)}
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                          title="Create a new draft version by pulling a site's current subscription flags"
                        >
                          <i className="fa-solid fa-arrows-rotate" />
                          Capture from Site
                        </button>
                      )}
                    </div>

                    {detail.versions.length === 0 && <p className="text-xs text-slate-400 italic">No versions yet.</p>}
                    {detail.versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700">
                            v{v.versionNumber}{v.patchNumber ? `.${v.patchNumber}` : ""}
                            <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${v.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                              {v.isPublished ? "Published" : "Draft"}
                            </span>
                          </p>
                          {v.changeNotes && <p className="text-xs text-slate-400 truncate">{v.changeNotes}</p>}
                          <p className="text-[10px] text-slate-300">{v.createdBy} · {new Date(v.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => setEditModal({ pkg: detail, version: v })}
                            className="px-2.5 py-1 rounded text-xs border border-slate-200 text-slate-600 hover:bg-white"
                          >
                            {v.isPublished ? "View Features" : "Edit Features"}
                          </button>
                          {v.isPublished && (
                            <button
                              onClick={() => setApplyModal({ pkg: detail, version: v })}
                              className="px-2.5 py-1 rounded text-xs bg-primary text-white hover:bg-primary-active"
                            >
                              Apply to Site
                            </button>
                          )}
                          {!v.isPublished && (
                            <button
                              onClick={() => handlePublish(v.id)}
                              className="px-2.5 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              Publish
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Upgrade Report ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button
          onClick={() => { if (!showReport) { setShowReport(true); loadReport(); } else setShowReport(!showReport); }}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
        >
          <i className="fa-solid fa-table-list text-slate-400 text-sm" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700">Upgrade Report</p>
            <p className="text-xs text-slate-400">See which sites are on outdated package versions and upgrade them in bulk.</p>
          </div>
          <i className={`fa-solid fa-chevron-right text-slate-400 text-xs transition-transform ${showReport ? "rotate-90" : ""}`} />
        </button>

        {showReport && (
          <div className="border-t border-slate-100 px-4 py-4 space-y-3">
            {/* Filters + refresh */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
                {(["outdated", "all"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setReportFilter(f)}
                    className={`px-3 py-1.5 font-medium transition ${reportFilter === f ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-50"}`}
                  >
                    {f === "outdated" ? "Outdated Only" : "All Sites"}
                  </button>
                ))}
              </div>

              {reportPackages.length > 0 && (
                <select
                  value={reportPkgFilter}
                  onChange={(e) => setReportPkgFilter(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
                  className="rounded-md border border-slate-200 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Packages</option>
                  {reportPackages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}

              <button
                onClick={loadReport}
                disabled={reportLoading}
                className="px-3 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className={`fa-solid fa-arrows-rotate text-xs ${reportLoading ? "fa-spin" : ""}`} />
                {reportLoading ? "Loading…" : "Refresh"}
              </button>
            </div>

            {reportLoading && (
              <p className="text-xs text-slate-400 py-4 text-center"><i className="fa-solid fa-spinner fa-spin mr-1" />Loading report…</p>
            )}

            {!reportLoading && reportRows.length === 0 && (
              <p className="text-xs text-slate-400 py-4 text-center italic">
                {reportFilter === "outdated" ? "All sites are on the latest version." : "No sites with packages found."}
              </p>
            )}

            {!reportLoading && reportRows.length > 0 && (
              <>
                {/* Select-all + action bar */}
                <div className="flex items-center justify-between px-1">
                  <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedSiteIds.size === reportRows.length}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedSiteIds(new Set(reportRows.map((r) => r.siteId)));
                        else setSelectedSiteIds(new Set());
                      }}
                      className="rounded"
                    />
                    Select all ({reportRows.length})
                  </label>

                  {selectedSiteIds.size > 0 && (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={resetOverridesOnUpgrade}
                          onChange={(e) => setResetOverridesOnUpgrade(e.target.checked)}
                          className="rounded"
                        />
                        Reset overrides
                      </label>
                      <button
                        onClick={handleUpgradeSelected}
                        disabled={upgrading}
                        className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary-active disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {upgrading && <i className="fa-solid fa-spinner fa-spin" />}
                        Upgrade Selected ({selectedSiteIds.size})
                      </button>
                    </div>
                  )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
                      <tr>
                        <th className="px-3 py-2 text-center w-8"></th>
                        <th className="px-3 py-2 text-left">Company</th>
                        <th className="px-3 py-2 text-left">Site ID</th>
                        <th className="px-3 py-2 text-left">Package</th>
                        <th className="px-3 py-2 text-left">Current</th>
                        <th className="px-3 py-2 text-left">Latest</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-center">Auto-Up</th>
                        <th className="px-3 py-2 text-center">Overrides</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reportRows.map((row) => (
                        <tr
                          key={row.siteId}
                          className={`hover:bg-slate-50 cursor-pointer ${selectedSiteIds.has(row.siteId) ? "bg-primary-bg" : ""}`}
                          onClick={() => setSelectedSiteIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.siteId)) next.delete(row.siteId);
                            else next.add(row.siteId);
                            return next;
                          })}
                        >
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedSiteIds.has(row.siteId)}
                              onChange={(e) => setSelectedSiteIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(row.siteId);
                                else next.delete(row.siteId);
                                return next;
                              })}
                              className="rounded"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium text-slate-700 max-w-[180px] truncate">{row.companyName ?? "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{row.siteId}</td>
                          <td className="px-3 py-2 text-slate-500">{row.packageName}</td>
                          <td className="px-3 py-2 text-slate-500">{row.currentVersionLabel || "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{row.latestVersionLabel || "—"}</td>
                          <td className="px-3 py-2 text-center">
                            {row.isLatest
                              ? <span className="inline-block px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium">Current</span>
                              : <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">Outdated</span>
                            }
                          </td>
                          <td className="px-3 py-2 text-center">
                            {row.autoUpgrade
                              ? <i className="fa-solid fa-check text-emerald-500" />
                              : <i className="fa-solid fa-xmark text-slate-300" />
                            }
                          </td>
                          <td className="px-3 py-2 text-center text-slate-500" onClick={e => e.stopPropagation()}>
                            {row.overrideCount > 0
                              ? <a
                                  href={`/admin/subscription-settings?siteId=${row.siteId}`}
                                  className="text-amber-600 font-medium hover:text-amber-800 hover:underline"
                                  title="View overrides in Subscription Settings"
                                >{row.overrideCount}</a>
                              : <span className="text-slate-300">0</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {editModal && (
        <FeatureEditorModal
          pkg={editModal.pkg}
          version={editModal.version}
          onClose={() => setEditModal(null)}
          onSaved={() => { if (expandedId) handleExpand(expandedId); }}
        />
      )}
      {applyModal && (
        <ApplyPackageModal
          pkg={applyModal.pkg}
          version={applyModal.version}
          onClose={() => setApplyModal(null)}
        />
      )}
      {showGuide && <PackagesGuidePanel onClose={() => setShowGuide(false)} />}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function BillingFinancial() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [activeTab, setActiveTab] = useState<DashTab>("upcoming");
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const resp = await api.messaging.get("superadmin/billing/financial-summary");
      if (resp.ok) setSummary(await resp.json());
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const TABS: { id: DashTab; label: string; icon: string }[] = [
    { id: "upcoming", label: "Upcoming", icon: "fa-calendar-days" },
    { id: "collections", label: "Collections", icon: "fa-file-invoice-dollar" },
    { id: "deferred", label: "Deferred Revenue", icon: "fa-calendar-check" },
    { id: "mrr", label: "MRR / ARR", icon: "fa-chart-line" },
    { id: "revenue", label: "Revenue Intel", icon: "fa-chart-bar" },
    { id: "quickbooks", label: "QuickBooks", icon: "fa-book" },
    { id: "health", label: "Health Check", icon: "fa-heart-pulse" },
    { id: "packages", label: "Packages", icon: "fa-boxes-stacked" },
    { id: "staxbill", label: "StaxBill Import", icon: "fa-arrow-right-arrow-left" },
    { id: "tax", label: "Sales Tax & Nexus", icon: "fa-landmark" },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Financial Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Deferred revenue, MRR/ARR, collections, QuickBooks, and billing imports.
            </p>
          </div>
          <button
            onClick={loadSummary}
            disabled={summaryLoading}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
            title="Refresh summary"
          >
            <i className={`fa-solid fa-arrows-rotate ${summaryLoading ? "fa-spin" : ""}`} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <i className={`fa-solid ${tab.icon} text-xs`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-5">
        {activeTab === "upcoming" && <UpcomingTab />}
        {activeTab === "collections" && <CollectionsTab summary={summary} />}
        {activeTab === "deferred" && (
          <DeferredTab summary={summary} api={api} toastAlert={toastAlert} />
        )}
        {activeTab === "mrr" && <MrrTab summary={summary} />}
        {activeTab === "revenue" && <RevenueDashboardTab />}
        {activeTab === "quickbooks" && (
          <QuickBooksTab summary={summary} onRefresh={loadSummary} />
        )}
        {activeTab === "health" && <BillingHealthTab />}
        {activeTab === "packages" && <PackagesTab />}
        {activeTab === "staxbill" && <StaxBillTab />}
        {activeTab === "tax" && <TaxNexusTab />}
      </div>
    </div>
  );
}

// ── TaxNexusTab ────────────────────────────────────────────────────────────────

interface TaxRateRow {
  stateCode: string;
  stateName: string;
  combinedRate: number;
  stateRate: number;
  averageLocalRate: number;
  nexusThresholdRevenue: number;
  nexusThresholdTx: number;
  nexusActive: boolean;
  nexusEstablishedAt?: string;
  hasPhysicalPresence: boolean;
  nexusReason?: string;
  trailing12mRevenue: number;
  trailing12mTx: number;
  taxOwed: number;
  totalCollected: number;
  totalRemitted: number;
  lastRemittance?: string;
}

function fmtPct(v: number) {
  return (v * 100).toFixed(2) + "%";
}

function fmtDollar(v: number) {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function TaxNexusTab() {
  const api = useApi();
  const toast = useToastAlert();
  const [rows, setRows] = useState<TaxRateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "active" | "approaching">("all");
  const [remitting, setRemitting] = useState<string | null>(null);
  const [remitAmt, setRemitAmt] = useState("");
  const [globalTaxEnabled, setGlobalTaxEnabled] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesRes, defaultsRes, syncRes] = await Promise.all([
        api.messaging.get("superadmin/billing/tax/rates"),
        api.messaging.get("superadmin/billing/defaults"),
        api.messaging.get("superadmin/billing/tax/sync-status"),
      ]);
      const rates = await ratesRes.json();
      const defaults = await defaultsRes.json();
      const syncStatus = await syncRes.json();
      setRows(Array.isArray(rates) ? rates : []);
      setGlobalTaxEnabled(defaults?.enableSalesTax ?? false);
      setLastSync(syncStatus?.lastSync ?? null);
    } catch {
      toast.displayToast(Severity.Error, "Failed to load tax data");
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await api.messaging.post("superadmin/billing/tax/sync", {});
      if (res.ok) {
        toast.displayToast(Severity.Success, "Tax rate sync enqueued — rates will update in ~30 seconds");
        setTimeout(() => load(), 5000);
      } else {
        const body = await res.json().catch(() => ({}));
        toast.displayToast(Severity.Error, (body as any)?.error ?? "Sync failed");
      }
    } catch {
      toast.displayToast(Severity.Error, "Network error triggering sync");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const toggleGlobalTax = async () => {
    setSavingGlobal(true);
    try {
      const next = !globalTaxEnabled;
      if (next && !confirm("Enable sales tax collection? Tax will be applied to all eligible invoices in nexus states.")) {
        setSavingGlobal(false); return;
      }
      const res = await api.messaging.put("superadmin/billing/defaults", { enableSalesTax: next });
      if (res.ok) {
        setGlobalTaxEnabled(next);
        toast.displayToast(Severity.Success, next ? "Sales tax collection enabled" : "Sales tax collection disabled");
      }
    } catch {
      toast.displayToast(Severity.Error, "Failed to update tax setting");
    } finally {
      setSavingGlobal(false);
    }
  };

  const togglePhysicalPresence = async (stateCode: string, current: boolean) => {
    const next = !current;
    if (next && !confirm(
      `Mark that Velaro has physical presence in ${stateCode}?\n\n` +
      `This activates tax collection in ${stateCode} immediately — even with $0 revenue.\n` +
      `Use this if you have a remote employee, office, or registered agent in this state.`
    )) return;
    try {
      const res = await api.messaging.put(`superadmin/billing/tax/nexus/${stateCode}/physical-presence`, { hasPhysicalPresence: next });
      if (res.ok) {
        toast.displayToast(Severity.Success, next
          ? `${stateCode} flagged as physical presence — tax collection now active`
          : `Physical presence flag removed from ${stateCode}`);
        await load();
      } else {
        const body = await res.json().catch(() => ({}));
        toast.displayToast(Severity.Error, (body as any)?.error ?? "Failed to update");
      }
    } catch {
      toast.displayToast(Severity.Error, "Network error");
    }
  };

  const recordRemittance = async (stateCode: string) => {
    const amt = parseFloat(remitAmt);
    if (isNaN(amt) || amt <= 0) { toast.displayToast(Severity.Error, "Enter a valid amount"); return; }
    try {
      const res = await api.messaging.post("superadmin/billing/tax/remittance", { stateCode, amountDollars: amt });
      if (res.ok) {
        toast.displayToast(Severity.Success, `Remittance of ${fmtDollar(amt)} recorded for ${stateCode}`);
        setRemitting(null);
        setRemitAmt("");
        await load();
      } else {
        toast.displayToast(Severity.Error, "Failed to record remittance");
      }
    } catch {
      toast.displayToast(Severity.Error, "Network error");
    }
  };

  const filtered = rows.filter((r) => {
    if (filter === "active") return r.nexusActive;
    if (filter === "approaching") {
      const revPct = r.trailing12mRevenue / r.nexusThresholdRevenue;
      const txPct = r.trailing12mTx / r.nexusThresholdTx;
      return !r.nexusActive && (revPct >= 0.5 || txPct >= 0.5);
    }
    return true;
  });

  const totalOwed = rows.reduce((s, r) => s + r.taxOwed, 0);
  const activeCount = rows.filter((r) => r.nexusActive).length;
  const approachingCount = rows.filter((r) => {
    const revPct = r.trailing12mRevenue / r.nexusThresholdRevenue;
    const txPct = r.trailing12mTx / r.nexusThresholdTx;
    return !r.nexusActive && (revPct >= 0.5 || txPct >= 0.5);
  }).length;

  if (loading) return <div className="p-6 text-slate-400 animate-pulse text-sm">Loading tax data…</div>;

  // Rates are stale if last sync was > 100 days ago or never synced at all
  const syncIsStale = !lastSync || (Date.now() - new Date(lastSync).getTime()) > 100 * 86400 * 1000;
  const syncLabel = lastSync
    ? `Rates last refreshed ${new Date(lastSync).toLocaleDateString()}`
    : "Rates have never been synced from TaxJar — using 2025 seed data";

  return (
    <div className="p-4 space-y-5">

      {/* TaxJar setup banner — shown when rates are stale or have never been synced */}
      {syncIsStale && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
          <i className="fa fa-triangle-exclamation text-amber-500 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800">
              {lastSync ? "Tax rates may be outdated" : "Tax rates are using 2025 seed data"}
            </p>
            <p className="text-amber-700 mt-0.5">
              {lastSync
                ? `Last synced ${new Date(lastSync).toLocaleDateString()} — more than 100 days ago.`
                : "Automatic quarterly sync requires a TaxJar API key."}
              {" "}To enable automatic quarterly updates, add{" "}
              <code className="bg-amber-100 px-1 rounded font-mono text-xs">TaxJar:ApiKey</code>{" "}
              to the <strong>velaro-messaging-api-staging</strong> App Settings in Azure
              (or the deploy workflow secrets). Get a free key at{" "}
              <strong>app.taxjar.com/api/keys</strong> (no credit card needed for summary rates).
            </p>
          </div>
          <button
            onClick={triggerSync}
            disabled={syncing}
            className="shrink-0 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {syncing ? "Queuing…" : "Sync Now"}
          </button>
        </div>
      )}

      {/* Global toggle + summary */}
      <div className="flex flex-wrap gap-4 items-start">
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 flex-1 min-w-[240px]">
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Sales Tax Collection</p>
            <p className="text-xs text-slate-500 mt-0.5">
              When enabled, tax is applied to invoices in nexus states.{" "}
              <span className="text-slate-400">{syncLabel}</span>
            </p>
          </div>
          <button
            onClick={toggleGlobalTax}
            disabled={savingGlobal}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              globalTaxEnabled ? "bg-blue-600" : "bg-slate-300"
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${globalTaxEnabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center min-w-[130px]">
          <p className="text-2xl font-bold text-red-600">{fmtDollar(totalOwed)}</p>
          <p className="text-xs text-slate-500 mt-0.5">Total Tax Owed</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center min-w-[130px]">
          <p className="text-2xl font-bold text-orange-600">{activeCount}</p>
          <p className="text-xs text-slate-500 mt-0.5">Nexus States</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center min-w-[130px]">
          <p className="text-2xl font-bold text-yellow-600">{approachingCount}</p>
          <p className="text-xs text-slate-500 mt-0.5">Approaching Nexus</p>
        </div>
        {!syncIsStale && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between min-w-[130px]">
            <p className="text-xs text-slate-500 mb-2">Rate data</p>
            <button
              onClick={triggerSync}
              disabled={syncing}
              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 disabled:opacity-50"
            >
              <i className="fa fa-arrows-rotate mr-1" />
              {syncing ? "Queuing…" : "Sync TaxJar"}
            </button>
            <p className="text-xs text-slate-400 mt-1">{lastSync ? new Date(lastSync).toLocaleDateString() : "seed data"}</p>
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {(["all", "active", "approaching"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === f ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {f === "all" ? "All States" : f === "active" ? `Active (${activeCount})` : `Approaching (${approachingCount})`}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">State</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Rate</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">12m Revenue</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Threshold</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Exposure</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Tax Owed</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => {
              const revPct = Math.min(1, r.trailing12mRevenue / Math.max(1, r.nexusThresholdRevenue));
              const txPct = Math.min(1, r.trailing12mTx / Math.max(1, r.nexusThresholdTx));
              const approaching = !r.nexusActive && (revPct >= 0.5 || txPct >= 0.5);
              return (
                <tr key={r.stateCode} className={r.nexusActive ? "bg-red-50/30" : approaching ? "bg-yellow-50/30" : ""}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-800">{r.stateCode}</div>
                    <div className="text-xs text-slate-400">{r.stateName}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-700 font-mono text-xs">
                    {r.combinedRate > 0 ? fmtPct(r.combinedRate) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="text-xs text-slate-700">{fmtDollar(r.trailing12mRevenue)}</div>
                    <div className="text-xs text-slate-400">{r.trailing12mTx} tx</div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="text-xs text-slate-500">{fmtDollar(r.nexusThresholdRevenue)}</div>
                    <div className="text-xs text-slate-400">{r.nexusThresholdTx} tx</div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="w-16 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${revPct >= 1 ? "bg-red-500" : revPct >= 0.75 ? "bg-orange-400" : revPct >= 0.5 ? "bg-yellow-400" : "bg-emerald-400"}`}
                          style={{ width: `${revPct * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 w-8 text-right">{Math.round(revPct * 100)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {r.taxOwed > 0 ? (
                      <span className="text-red-600 font-semibold text-xs">{fmtDollar(r.taxOwed)}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex flex-col items-center gap-1">
                      {r.nexusActive && r.nexusReason === "physical_presence" ? (
                        <span className="inline-flex items-center bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Physical
                        </span>
                      ) : r.nexusActive ? (
                        <span className="inline-flex items-center bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Economic
                        </span>
                      ) : approaching ? (
                        <span className="inline-flex items-center bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Approaching
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                      {/* Physical presence toggle */}
                      <button
                        title={r.hasPhysicalPresence ? "Remove physical presence flag" : "Mark: Velaro has physical presence here"}
                        onClick={() => togglePhysicalPresence(r.stateCode, r.hasPhysicalPresence)}
                        className={`text-xs px-1.5 py-0.5 rounded border transition-colors ${
                          r.hasPhysicalPresence
                            ? "border-purple-300 bg-purple-50 text-purple-700"
                            : "border-slate-200 text-slate-400 hover:border-purple-300 hover:text-purple-600"
                        }`}
                      >
                        <i className="fa fa-building mr-1" />
                        {r.hasPhysicalPresence ? "Office/Employee" : "+ presence"}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {r.nexusActive && r.taxOwed > 0 && (
                      remitting === r.stateCode ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            placeholder="Amount"
                            value={remitAmt}
                            onChange={(e) => setRemitAmt(e.target.value)}
                            className="w-24 border border-slate-300 rounded px-2 py-1 text-xs"
                          />
                          <button onClick={() => recordRemittance(r.stateCode)} className="text-xs text-emerald-600 font-medium">Save</button>
                          <button onClick={() => { setRemitting(null); setRemitAmt(""); }} className="text-xs text-slate-400">✕</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setRemitting(r.stateCode); setRemitAmt(""); }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Record remittance
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-slate-400 text-sm">
            No states match the current filter.
          </div>
        )}
      </div>

      {/* DOR Remittance Calendar */}
      {activeCount > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">
            <i className="fa fa-calendar-days mr-2 text-slate-400" />
            DOR Remittance Calendar
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            {[
              { label: "Q1 — Jan 20", desc: "Oct–Dec collections due" },
              { label: "Q2 — Apr 20", desc: "Jan–Mar collections due" },
              { label: "Q3 — Jul 20", desc: "Apr–Jun collections due" },
              { label: "Q4 — Oct 20", desc: "Jul–Sep collections due" },
            ].map((q) => (
              <div key={q.label} className="border border-slate-100 rounded-lg p-3 bg-slate-50">
                <p className="font-semibold text-slate-700">{q.label}</p>
                <p className="text-slate-500 mt-0.5">{q.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Most states are quarterly. CA, TX, NY, FL, and IL switch you to <strong>monthly</strong> filing
            once annual collections in that state exceed roughly $1,200 — that's a filing-frequency rule,
            not a collection-start rule. Physical presence creates nexus from dollar one regardless of revenue.
            Use the "Record Remittance" action above after filing with each state's DOR portal.
          </p>
        </div>
      )}

      <p className="text-xs text-slate-400">
        Rates seeded from 2025 TaxFoundation/SST data. Quarterly auto-sync from TaxJar when{" "}
        <code className="bg-slate-100 px-1 rounded font-mono">TaxJar:ApiKey</code> is configured.
        Economic nexus threshold: $100K revenue OR 200 transactions (trailing 12 months) for most states.
        CA/NY: $500K or 100 transactions. MS: $250K.
        Consult a tax professional before remitting.
      </p>
    </div>
  );
}
